// components/AdminPanel.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import {
    Cpu, Database, Shield, Lock, Trash2, Edit2, Activity, Plus, RefreshCw,
    Layers, Users, Building2, PieChart, FileText, CheckCircle, XCircle, AlertCircle,
    ChevronRight, ChevronLeft, MoreVertical, Download, Link, Settings, Share2, Eye, EyeOff,
    Save, Check, FolderOpen, FileUp, Key, Copy, X, UserPlus, Globe, Plug, Zap, Server, ChevronDown, Send
} from 'lucide-react';
import AssetAccessModal from './AssetAccessModal';
// import ReviewModal from './ReviewModal';

const AdminPanel = ({ onClose, isFullPage }) => {
    const { user, checkAuth } = useAuth();
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewDashboard, setReviewDashboard] = useState(null);
    const isBuilder = user?.role === 'builder';
    const { showToast, showConfirm } = useUI();
    const isPlatformAdmin = user?.org_id === 'saturniq';
    const isWorkspaceAdmin = user?.role === 'workspace_admin';
    const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';
    const [activeTab, setActiveTab] = useState(isBuilder && !isWorkspaceAdmin && !isSuperAdmin ? 'database' : (isWorkspaceAdmin ? 'workspaces' : (isPlatformAdmin || isSuperAdmin ? 'llm' : 'users'))); // llm, users, database, governance, organizations
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState('');

    // Environment / connection
    const isProd = process.env.NODE_ENV === 'production';
    const API_BASE = process.env.REACT_APP_API_BASE || (isProd ? '' : 'http://localhost:4000/api');

    // --- States for each section ---

    // User Management
    const [users, setUsers] = useState([]);

    // Organization Management
    const [organizations, setOrganizations] = useState([]);
    const [newOrgName, setNewOrgName] = useState('');
    const [newOrgPlan, setNewOrgPlan] = useState('starter');
    const [newOrgMaxWS, setNewOrgMaxWS] = useState(3);
    const [newOrgMaxUsers, setNewOrgMaxUsers] = useState(50);
    const [newOrgAdminEmail, setNewOrgAdminEmail] = useState('');
    const [newOrgAdminName, setNewOrgAdminName] = useState('');
    const [newOrgAdminPassword, setNewOrgAdminPassword] = useState('');
    const [showAdminPassword, setShowAdminPassword] = useState(false);
    const [showCreateOrg, setShowCreateOrg] = useState(false);
    const [editingOrg, setEditingOrg] = useState(null); // { id, name, plan, max_workspaces, max_users }
    const [copiedCode, setCopiedCode] = useState(null);

    // Workspace Management
    const [workspaces, setWorkspaces] = useState([]);
    const [newWsName, setNewWsName] = useState('');
    const [newWsDesc, setNewWsDesc] = useState('');
    const [showCreateWs, setShowCreateWs] = useState(false);
    const [editingWorkspace, setEditingWorkspace] = useState(null); // { id, name, description }

    // Workspace Members
    const [memberModalWs, setMemberModalWs] = useState(null); // workspace obj
    const [wsMembers, setWsMembers] = useState([]);
    const [addMemberUserId, setAddMemberUserId] = useState('');
    const [addMemberRole, setAddMemberRole] = useState('viewer');
    const [orgUsers, setOrgUsers] = useState([]);

    // User → Workspace Admin mapping
    const [userWsMap, setUserWsMap] = useState({}); // { userId: [{ workspace_id, ws_name, ws_role }] }
    const [wsPickerUserId, setWsPickerUserId] = useState(null);

    // DB Connection
    const [dbConfig, setDbConfig] = useState({
        type: 'PostgreSQL',
        host: 'localhost',
        port: '5432',
        database: '',
        username: '',
        password: ''
    });
    const [dbTestResult, setDbTestResult] = useState(null);

    // Data Connections (Multi-Source)
    const [dataConnections, setDataConnections] = useState([]);
    const [showAddConnection, setShowAddConnection] = useState(false);
    const [showMainDbForm, setShowMainDbForm] = useState(false);
    const [newConn, setNewConn] = useState({
        name: '',
        source_type: 'postgresql',
        host: '',
        port: '5432',
        database: '',
        user: '',
        password: '',
        // REST API
        base_url: '',
        auth_type: 'none',
        token: '',
        api_key_header: '',
        api_key_value: '',
        endpoints: [{ name: '', path: '', method: 'GET', response_path: '' }],
        // Cloud App
        cloud_type: 'servicenow',
        instance_url: '',
        client_id: '',
        client_secret: ''
    });
    const [connTestResult, setConnTestResult] = useState(null);
    const [syncingId, setSyncingId] = useState(null);
    const [uploadConnectionId, setUploadConnectionId] = useState('main'); // 'main' or connectionId

    // Data Governance
    const [assets, setAssets] = useState([]);
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [showAccessModal, setShowAccessModal] = useState(false);
    const [govFilter, setGovFilter] = useState('all'); // all, table, chart, dashboard

    // Upload Progress
    const [uploadProgress, setUploadProgress] = useState({ active: false, percent: 0, fileName: '', status: '' });

    // LLM Config
    const [llmConfig, setLlmConfig] = useState({
        provider: 'anthropic',
        model: 'claude-sonnet-4',
        apiKey: ''
    });
    const [showApiKey, setShowApiKey] = useState(false);

    const MODELS = [
        { id: 'claude-sonnet-4', name: 'Claude Sonnet 4', provider: 'anthropic', desc: 'Most capable, balanced performance' },
        { id: 'claude-opus-4', name: 'Claude Opus 4', provider: 'anthropic', desc: 'Highest intelligence, best for complex tasks' },
        { id: 'claude-haiku-4', name: 'Claude Haiku 4', provider: 'anthropic', desc: 'Fastest, most cost-effective' },
        { id: 'gpt-4', name: 'GPT-4', provider: 'openai', desc: 'Advanced reasoning and analysis' },
        { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', provider: 'openai', desc: 'Faster, more efficient GPT-4' }
    ];

    // Load initial data based on active tab
    const fetchUsers = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/users`, { credentials: 'include' });
            const data = await res.json();
            if (res.ok) setUsers(data.users || []);
            else setError(data.error);
        } catch (err) {
            setError('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    const fetchAssets = React.useCallback(async (refresh = false) => {
        setLoading(true);
        try {
            const url = refresh ? `${API_BASE}/admin/assets?refresh=true` : `${API_BASE}/admin/assets`;
            const res = await fetch(url, { credentials: 'include' });
            const data = await res.json();
            if (res.ok) setAssets(data.assets || []);
            else setError(data.error);
        } catch (err) {
            setError('Failed to fetch data assets');
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    const fetchSettings = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/admin/settings`, { credentials: 'include' });
            if (!res.ok) throw new Error('Failed to fetch settings');
            const data = await res.json();

            // Parse settings
            if (data.settings?.llm_config) {
                setLlmConfig(data.settings.llm_config);
            }
            // DB config (might be masked)
            if (data.dbConfig) {
                setDbConfig(prev => ({
                    ...prev,
                    host: data.dbConfig.host,
                    port: data.dbConfig.port,
                    database: data.dbConfig.database,
                    username: data.dbConfig.user,
                    password: ''
                }));
            }
        } catch (err) {
            console.error(err);
            setError('Failed to fetch settings');
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    // Fetch organizations (Platform admin only)
    const fetchOrganizations = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/organizations`, { credentials: 'include' });
            const data = await res.json();
            if (res.ok) setOrganizations(data.organizations || []);
            else setError(data.error);
        } catch (err) {
            setError('Failed to fetch organizations');
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    // Create organization
    const handleCreateOrg = async () => {
        if (!newOrgName.trim()) {
            showToast('Organization name is required', 'error');
            return;
        }
        if (!newOrgAdminEmail.trim()) {
            showToast('Admin email is required', 'error');
            return;
        }
        if (!newOrgAdminPassword || newOrgAdminPassword.length < 6) {
            showToast('Admin password must be at least 6 characters', 'error');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/organizations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    name: newOrgName.trim(),
                    plan: newOrgPlan,
                    max_workspaces: newOrgMaxWS,
                    max_users: newOrgMaxUsers,
                    admin_email: newOrgAdminEmail.trim(),
                    admin_name: newOrgAdminName.trim() || undefined,
                    admin_password: newOrgAdminPassword
                })
            });
            const data = await res.json();
            if (res.ok) {
                showToast(`Organization "${newOrgName}" created! Admin: ${data.admin.email} | Invite: ${data.organization.invite_code} `, 'success');
                setNewOrgName('');
                setNewOrgPlan('starter');
                setNewOrgMaxWS(3);
                setNewOrgMaxUsers(50);
                setNewOrgAdminEmail('');
                setNewOrgAdminName('');
                setNewOrgAdminPassword('');
                setShowCreateOrg(false);
                fetchOrganizations();
            } else {
                showToast(data.error || 'Failed to create organization', 'error');
            }
        } catch (err) {
            showToast('Error creating organization', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Delete organization
    const handleDeleteOrg = async (orgId, orgName) => {
        const confirmed = await showConfirm(`Delete organization "${orgName}" ? This cannot be undone.`);
        if (!confirmed) return;

        try {
            const res = await fetch(`${API_BASE}/auth/organizations/${orgId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            const data = await res.json();
            if (res.ok) {
                showToast('Organization deleted', 'success');
                fetchOrganizations();
            } else {
                showToast(data.error || 'Failed to delete', 'error');
            }
        } catch (err) {
            showToast('Error deleting organization', 'error');
        }
    };

    const handleSubmitForReview = async (dashboardId, reviewerIds) => {
        try {
            const res = await fetch(`${API_BASE}/dashboards/${dashboardId}/submit-for-review`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ reviewerIds })
            });
            const data = await res.json();
            if (res.ok) {
                showToast('Dashboard submitted for review!', 'success');
                setShowReviewModal(false);
                if (activeTab === 'governance') fetchAssets(true);
            } else {
                showToast(data.error || 'Failed to submit for review', 'error');
            }
        } catch (err) {
            showToast('Error submitting for review', 'error');
        }
    };

    // Update organization
    const handleUpdateOrg = async () => {
        if (!editingOrg) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/organizations/${editingOrg.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    new_id: editingOrg.new_id !== undefined ? editingOrg.new_id : editingOrg.id,
                    name: editingOrg.name,
                    plan: editingOrg.plan,
                    max_workspaces: parseInt(editingOrg.max_workspaces),
                    max_users: parseInt(editingOrg.max_users)
                })
            });
            const data = await res.json();
            if (res.ok) {
                showToast(`Organization updated!`, 'success');
                setEditingOrg(null);
                fetchOrganizations();
            } else {
                showToast(data.error || 'Failed to update organization', 'error');
            }
        } catch (err) {
            showToast('Error updating organization', 'error');
        } finally {
            setLoading(false);
        }
    };
    const handleRegenerateInvite = async (orgId) => {
        try {
            const res = await fetch(`${API_BASE}/auth/organizations/${orgId}/regenerate-invite`, {
                method: 'POST',
                credentials: 'include'
            });
            const data = await res.json();
            if (res.ok) {
                showToast(`New invite code: ${data.invite_code}`, 'success');
                if (orgId === user?.org_id) {
                    await checkAuth();
                } else if (isPlatformAdmin) {
                    fetchOrganizations();
                }
            } else {
                showToast(data.error || 'Failed to regenerate', 'error');
            }
        } catch (err) {
            showToast('Error regenerating invite', 'error');
        }
    };

    // Copy invite code
    const copyInviteCode = (code) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        showToast('Invite code copied!', 'success');
        setTimeout(() => setCopiedCode(null), 2000);
    };



    // Workspace functions
    const fetchWorkspaces = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/workspaces`, { credentials: 'include' });
            const data = await res.json();
            if (res.ok) setWorkspaces(data.workspaces || []);
            else setError(data.error);
        } catch (err) {
            setError('Failed to fetch workspaces');
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    const handleCreateWorkspace = async () => {
        if (!newWsName.trim()) {
            showToast('Workspace name is required', 'error');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/workspaces`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ name: newWsName.trim(), description: newWsDesc.trim() })
            });
            const data = await res.json();
            if (res.ok) {
                showToast(`Workspace "${newWsName}" created!`, 'success');
                setNewWsName('');
                setNewWsDesc('');
                setShowCreateWs(false);
                fetchWorkspaces();
            } else {
                showToast(data.error || 'Failed to create workspace', 'error');
            }
        } catch (err) {
            showToast('Error creating workspace', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteWorkspace = async (wsId, wsName) => {
        const confirmed = await showConfirm(`Delete workspace "${wsName}"? This cannot be undone.`);
        if (!confirmed) return;
        try {
            const res = await fetch(`${API_BASE}/auth/workspaces/${wsId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            const data = await res.json();
            if (res.ok) {
                showToast('Workspace deleted', 'success');
                fetchWorkspaces();
            } else {
                showToast(data.error || 'Failed to delete', 'error');
            }
        } catch (err) {
            showToast('Error deleting workspace', 'error');
        }
    };

    const handleUpdateWorkspace = async () => {
        if (!editingWorkspace) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/workspaces/${editingWorkspace.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    name: editingWorkspace.name,
                    description: editingWorkspace.description
                })
            });
            const data = await res.json();
            if (res.ok) {
                showToast(`Workspace updated!`, 'success');
                setEditingWorkspace(null);
                fetchWorkspaces();
            } else {
                showToast(data.error || 'Failed to update workspace', 'error');
            }
        } catch (err) {
            showToast('Error updating workspace', 'error');
        } finally {
            setLoading(false);
        }
    };

    // --- Workspace Member Management ---

    const openMemberModal = async (ws) => {
        setMemberModalWs(ws);
        setAddMemberUserId('');
        setAddMemberRole('viewer');
        // Fetch members + org users in parallel
        try {
            const [membersRes, usersRes] = await Promise.all([
                fetch(`${API_BASE}/auth/workspaces/${ws.id}/members`, { credentials: 'include' }),
                fetch(`${API_BASE}/auth/users`, { credentials: 'include' })
            ]);
            const membersData = await membersRes.json();
            const usersData = await usersRes.json();
            setWsMembers(membersData.members || []);
            setOrgUsers(usersData.users || []);
        } catch (err) {
            showToast('Error loading members', 'error');
        }
    };

    const handleAddMember = async () => {
        if (!addMemberUserId || !memberModalWs) return;
        try {
            const res = await fetch(`${API_BASE}/auth/workspaces/${memberModalWs.id}/members`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ user_id: parseInt(addMemberUserId), role: addMemberRole })
            });
            const data = await res.json();
            if (res.ok) {
                showToast(data.message || 'Member added', 'success');
                setAddMemberUserId('');
                // Refresh members
                const membersRes = await fetch(`${API_BASE}/auth/workspaces/${memberModalWs.id}/members`, { credentials: 'include' });
                const membersData = await membersRes.json();
                setWsMembers(membersData.members || []);
                fetchWorkspaces(); // update count
            } else {
                showToast(data.error || 'Failed to add member', 'error');
            }
        } catch (err) {
            showToast('Error adding member', 'error');
        }
    };

    const handleRemoveMember = async (userId, userName) => {
        if (!memberModalWs) return;
        const confirmed = await showConfirm(`Remove ${userName} from this workspace?`);
        if (!confirmed) return;
        try {
            const res = await fetch(`${API_BASE}/auth/workspaces/${memberModalWs.id}/members/${userId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (res.ok) {
                showToast('Member removed', 'success');
                setWsMembers(prev => prev.filter(m => m.user_id !== userId));
                fetchWorkspaces();
            } else {
                const data = await res.json();
                showToast(data.error || 'Failed to remove', 'error');
            }
        } catch (err) {
            showToast('Error removing member', 'error');
        }
    };

    // --- Actions ---

    const handleSaveSettings = async (key, value) => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/admin/settings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ key, value })
            });
            if (res.ok) {
                showToast('Settings saved successfully', 'success');
                setSuccessMsg('Settings saved successfully');
            } else {
                showToast('Failed to save settings', 'error');
                setError('Failed to save settings');
            }
        } catch (err) {
            showToast('Error saving settings', 'error');
            setError('Error saving settings');
        } finally {
            setLoading(false);
        }
    };

    const handleTestDb = async () => {
        setLoading(true);
        setDbTestResult(null);
        try {
            const res = await fetch(`${API_BASE}/admin/test-db`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    host: dbConfig.host,
                    port: dbConfig.port,
                    database: dbConfig.database,
                    user: dbConfig.username,
                    password: dbConfig.password
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setDbTestResult({ success: true, message: 'Connection Successful!' });
            } else {
                setDbTestResult({ success: false, message: data.error || 'Connection Failed' });
            }
        } catch (err) {
            setDbTestResult({ success: false, message: 'Network Error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDbConfig = async () => {
        const confirmed = await showConfirm({
            title: 'Update Database Configuration',
            message: 'Saving will update the application configuration and typically requires a restart to take full effect. Continue?',
            confirmText: 'Save & Restart',
            cancelText: 'Cancel'
        });
        if (!confirmed) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/admin/save-db-config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    host: dbConfig.host,
                    port: dbConfig.port,
                    database: dbConfig.database,
                    user: dbConfig.username,
                    password: dbConfig.password
                })
            });
            const data = await res.json();
            if (res.ok) {
                showToast(data.message || 'DB config saved', 'success');
                setSuccessMsg(data.message);
            } else {
                showToast(data.error || 'Failed to save DB config', 'error');
                setError(data.error);
            }
        } catch (err) {
            showToast('Failed to save DB config', 'error');
            setError('Failed to save DB config');
        } finally {
            setLoading(false);
        }
    };

    // === DATA CONNECTIONS (Multi-Source) ===

    const fetchConnections = async () => {
        try {
            const res = await fetch(`${API_BASE}/connections`, { credentials: 'include' });
            const data = await res.json();
            if (res.ok) setDataConnections(data.connections || []);
        } catch (err) {
            console.error('Error fetching connections:', err);
        }
    };

    const handleTestNewConnection = async () => {
        setLoading(true);
        setConnTestResult(null);
        try {
            let connection_config = {};
            if (newConn.source_type === 'postgresql') {
                connection_config = { host: newConn.host, port: newConn.port, database: newConn.database, user: newConn.user, password: newConn.password };
            } else if (newConn.source_type === 'rest_api') {
                connection_config = { base_url: newConn.base_url, auth_type: newConn.auth_type, token: newConn.token, api_key_header: newConn.api_key_header, api_key_value: newConn.api_key_value, endpoints: newConn.endpoints.filter(e => e.path) };
            } else if (newConn.source_type === 'cloud_app') {
                connection_config = { base_url: newConn.instance_url, auth_type: 'bearer', token: newConn.client_secret };
            }

            const res = await fetch(`${API_BASE}/connections/test`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ source_type: newConn.source_type === 'cloud_app' ? 'rest_api' : newConn.source_type, connection_config })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setConnTestResult({ success: true, message: 'Connection successful!' });
            } else {
                setConnTestResult({ success: false, message: data.error || 'Connection failed' });
            }
        } catch (err) {
            setConnTestResult({ success: false, message: 'Network error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveConnection = async () => {
        if (!newConn.name) { showToast('Please enter a connection name', 'warning'); return; }
        setLoading(true);
        try {
            let connection_config = {};
            if (newConn.source_type === 'postgresql') {
                connection_config = { host: newConn.host, port: newConn.port, database: newConn.database, user: newConn.user, password: newConn.password };
            } else if (newConn.source_type === 'rest_api') {
                connection_config = { base_url: newConn.base_url, auth_type: newConn.auth_type, token: newConn.token, api_key_header: newConn.api_key_header, api_key_value: newConn.api_key_value, endpoints: newConn.endpoints.filter(e => e.path) };
            } else if (newConn.source_type === 'cloud_app') {
                connection_config = { cloud_type: newConn.cloud_type, instance_url: newConn.instance_url, client_id: newConn.client_id, client_secret: newConn.client_secret, base_url: newConn.instance_url, auth_type: 'bearer', token: newConn.client_secret, endpoints: [] };
            }

            const res = await fetch(`${API_BASE}/connections`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ name: newConn.name, source_type: newConn.source_type, connection_config })
            });
            const data = await res.json();
            if (res.ok) {
                showToast(`Connection "${newConn.name}" created! Syncing schema...`, 'success');
                setShowAddConnection(false);
                setNewConn({ name: '', source_type: 'postgresql', host: '', port: '5432', database: '', user: '', password: '', base_url: '', auth_type: 'none', token: '', api_key_header: '', api_key_value: '', endpoints: [{ name: '', path: '', method: 'GET', response_path: '' }], cloud_type: 'servicenow', instance_url: '', client_id: '', client_secret: '' });
                setConnTestResult(null);
                fetchConnections();
                // Auto-sync
                if (data.connection?.id) {
                    handleSyncConnection(data.connection.id);
                }
            } else {
                showToast(data.error || 'Failed to save connection', 'error');
            }
        } catch (err) {
            showToast('Failed to save connection', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSyncConnection = async (connId) => {
        setSyncingId(connId);
        try {
            const res = await fetch(`${API_BASE}/connections/${connId}/sync`, {
                method: 'POST',
                credentials: 'include'
            });
            const data = await res.json();
            if (res.ok) {
                showToast(`Schema synced: ${data.tables?.length || 0} tables found`, 'success');
                fetchConnections();
            } else {
                showToast(data.error || 'Sync failed', 'error');
            }
        } catch (err) {
            showToast('Sync failed', 'error');
        } finally {
            setSyncingId(null);
        }
    };

    const handleDeleteConnection = async (connId, connName) => {
        const confirmed = await showConfirm({
            title: 'Delete Data Source',
            message: `Are you sure you want to remove "${connName}"? All imported table references will be removed.`,
            confirmText: 'Delete',
            cancelText: 'Cancel'
        });
        if (!confirmed) return;
        try {
            const res = await fetch(`${API_BASE}/connections/${connId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (res.ok) {
                showToast(`Connection "${connName}" deleted`, 'success');
                fetchConnections();
            } else {
                const data = await res.json();
                showToast(data.error || 'Delete failed', 'error');
            }
        } catch (err) {
            showToast('Delete failed', 'error');
        }
    };

    const deleteUser = async (userId) => {
        const confirmed = await showConfirm({
            title: 'Delete User',
            message: 'Are you sure you want to delete this user? This action cannot be undone.',
            confirmText: 'Delete',
            cancelText: 'Cancel'
        });
        if (!confirmed) return;

        try {
            const res = await fetch(`${API_BASE}/admin/users/${userId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (res.ok) {
                setUsers(users.filter(u => u.id !== userId));
                showToast('User deleted successfully', 'success');
            } else {
                showToast('Failed to delete user', 'error');
            }
        } catch (err) {
            showToast('Error deleting user', 'error');
        }
    }

    const updateUserRole = async (userId, newRole) => {
        // If workspace_admin selected, open workspace picker instead of changing role
        if (newRole === 'workspace_admin') {
            setWsPickerUserId(wsPickerUserId === userId ? null : userId);
            return;
        }
        setWsPickerUserId(null);
        try {
            const res = await fetch(`${API_BASE}/admin/users/${userId}/role`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ role: newRole })
            });

            if (res.ok) {
                setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
                showToast('User role updated', 'success');
            } else {
                const data = await res.json();
                showToast(data.error || "Failed to update role", 'error');
            }
        } catch (err) {
            showToast("Error updating role", 'error');
        }
    };

    // Fetch all workspace memberships for all users
    const fetchAllUserWsMemberships = React.useCallback(async () => {
        try {
            const wsRes = await fetch(`${API_BASE}/auth/workspaces`, { credentials: 'include' });
            const wsData = await wsRes.json();
            const allWs = wsData.workspaces || [];

            const map = {};
            for (const ws of allWs) {
                try {
                    const membersRes = await fetch(`${API_BASE}/auth/workspaces/${ws.id}/members`, { credentials: 'include' });
                    const membersData = await membersRes.json();
                    for (const m of (membersData.members || [])) {
                        if (!map[m.user_id]) map[m.user_id] = [];
                        map[m.user_id].push({ workspace_id: ws.id, ws_name: ws.name, ws_role: m.ws_role });
                    }
                } catch (_) { }
            }
            setUserWsMap(map);
        } catch (err) {
            console.log('Error fetching ws memberships:', err);
        }
    }, [API_BASE]);

    // Assign user as workspace admin from User Management
    const assignWsAdmin = async (userId, wsId) => {
        try {
            // 1. Add to workspace as admin
            const res = await fetch(`${API_BASE}/auth/workspaces/${wsId}/members`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ user_id: userId, role: 'admin' })
            });
            const data = await res.json();

            if (res.ok) {
                // 2. Auto-promote org role to 'workspace_admin' if they are viewer/builder
                const currentUser = users.find(u => u.id === userId);
                if (currentUser && currentUser.role !== 'super_admin' && currentUser.role !== 'admin') {
                    await updateUserRole(userId, 'workspace_admin');
                }

                showToast(data.message || 'Workspace Admin assigned', 'success');
                setWsPickerUserId(null);
                fetchAllUserWsMemberships();
            } else {
                showToast(data.error || 'Failed to assign', 'error');
            }
        } catch (err) {
            showToast('Error assigning workspace admin', 'error');
        }
    };

    // Remove workspace admin from User Management
    const removeWsAdmin = async (userId, wsId, wsName) => {
        const confirmed = await showConfirm(`Remove workspace admin from "${wsName}"?`);
        if (!confirmed) return;
        try {
            const res = await fetch(`${API_BASE}/auth/workspaces/${wsId}/members/${userId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (res.ok) {
                showToast('Workspace admin removed', 'success');
                fetchAllUserWsMemberships();
            } else {
                const data = await res.json();
                showToast(data.error || 'Failed to remove', 'error');
            }
        } catch (err) {
            showToast('Error removing workspace admin', 'error');
        }
    };

    useEffect(() => {
        setError(null);
        setSuccessMsg('');
        if (activeTab === 'users') {
            checkAuth(); // Refresh user profile to get invite_code
            fetchUsers();
            fetchWorkspaces();
            fetchAllUserWsMemberships();
        }
        if (activeTab === 'governance') {
            fetchAssets();
            // Fetch org users for access modal — uses org-members endpoint accessible by all roles
            fetch(`${API_BASE}/users/org-members`, { credentials: 'include' })
                .then(res => res.json())
                .then(data => { if (data.users) setUsers(data.users); })
                .catch(() => { });
        }
        if (activeTab === 'llm' || activeTab === 'database') { fetchSettings(); fetchConnections(); }
        if (activeTab === 'organizations') fetchOrganizations();
        if (activeTab === 'workspaces') fetchWorkspaces();
    }, [activeTab, checkAuth, fetchUsers, fetchAssets, fetchSettings, fetchOrganizations, fetchWorkspaces, fetchAllUserWsMemberships]);

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (!files.length) return;

        const totalFiles = files.length;
        let successCount = 0;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);

            await new Promise((resolve) => {
                setUploadProgress({ active: true, percent: 0, fileName: `(${i + 1}/${totalFiles}) ${file.name} (${fileSizeMB} MB)`, status: 'Starting upload...' });

                const formData = new FormData();
                formData.append('file', file);
                formData.append('connectionId', uploadConnectionId);

                const xhr = new XMLHttpRequest();

                xhr.upload.addEventListener('progress', (e) => {
                    if (e.lengthComputable) {
                        const pct = Math.round((e.loaded / e.total) * 100);
                        setUploadProgress(prev => ({ ...prev, percent: pct, status: pct < 100 ? `Uploading... ${pct}%` : 'Processing on server...' }));
                    }
                });

                xhr.addEventListener('load', () => {
                    try {
                        const data = JSON.parse(xhr.responseText);
                        if (xhr.status >= 200 && xhr.status < 300) {
                            successCount++;
                            setUploadProgress({ active: true, percent: 100, fileName: `(${i + 1}/${totalFiles}) ${file.name}`, status: `✅ Table "${data.tableName}" created (${data.rowCount} rows)` });
                            showToast(`Table created: ${data.tableName} (File ${i + 1}/${totalFiles})`, 'success');
                            if (activeTab === 'governance') fetchAssets();
                            else if (activeTab === 'database') { fetchSettings(); fetchConnections(); }
                        } else {
                            setUploadProgress(prev => ({ ...prev, percent: 0, status: `❌ ${data.error || 'Upload failed'}` }));
                            showToast(data.error || `Upload failed for ${file.name}`, 'error');
                        }
                    } catch (e) {
                        setUploadProgress(prev => ({ ...prev, percent: 0, status: '❌ Unexpected server response' }));
                        showToast(`Error processing ${file.name}`, 'error');
                    }
                    setTimeout(() => resolve(), 2000);
                });

                xhr.addEventListener('error', () => {
                    setUploadProgress({ active: true, percent: 0, fileName: file.name, status: '❌ Network error during upload' });
                    showToast(`Network error uploading ${file.name}`, 'error');
                    setTimeout(() => resolve(), 2000);
                });

                xhr.open('POST', `${API_BASE}/upload-excel`);
                xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('authToken')}`);
                xhr.withCredentials = true;
                xhr.send(formData);
            });
        }

        event.target.value = '';
        setTimeout(() => setUploadProgress({ active: false, percent: 0, fileName: '', status: '' }), 1000);
    };

    const handleDeleteAsset = async (asset) => {
        const confirmed = await showConfirm({
            title: `Delete ${asset.asset_type.charAt(0).toUpperCase() + asset.asset_type.slice(1)}`,
            message: `Are you sure you want to permanently delete "${asset.asset_name}"? This action cannot be undone.`,
            confirmText: 'Delete Forever',
            cancelText: 'Cancel'
        });
        if (!confirmed) return;

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/admin/assets/${asset.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                credentials: 'include'
            });

            if (res.ok) {
                showToast('Asset deleted successfully', 'success');
                fetchAssets();
            } else {
                const data = await res.json();
                showToast(data.error || 'Failed to delete asset', 'error');
            }
        } catch (err) {
            showToast('Error deleting asset', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePermissions = async (assetId, isPublic, shares) => {
        try {
            const isBulk = assetId === 'bulk';
            const endpoint = isBulk ? `${API_BASE}/admin/assets/bulk-permissions` : `${API_BASE}/admin/asset/permissions`;
            const payload = isBulk ? { assetIds: selectedAssets, isPublic, shares } : { assetId, isPublic, shares };

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                // Refresh assets list
                fetchAssets();
                if (isBulk) setSelectedAssets([]); // Clear selection after bulk update
                showToast(`Permissions updated successfully`, 'success');
                return true;
            } else {
                const data = await res.json();
                showToast(data.error || 'Failed to update permissions', 'error');
                return false;
            }
        } catch (err) {
            showToast('Error updating permissions', 'error');
            return false;
        }
    };


    // --- Render Helpers ---

    const content = (
        <div className={`admin-panel-layout ${isFullPage ? 'full-page' : ''}`} onClick={e => e.stopPropagation()}>

            {/* Sidebar */}
            <div className="admin-sidebar" style={{ background: 'var(--bg-secondary)', borderRight: '1px solid var(--glass-border)' }}>
                <div className="sidebar-header" style={{ padding: '24px 20px', borderBottom: '1px solid var(--glass-border)' }}>
                    <div className="header-title-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <Settings size={24} style={{ color: 'var(--accent-primary)' }} />
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--text-main)', margin: 0 }}>Settings</h2>
                    </div>
                    <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: '0.9rem' }}>Configure workspace</p>
                </div>

                <nav className="sidebar-nav" style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {/* Hide User Management for builder */}
                    {!(isBuilder && !isWorkspaceAdmin && !isSuperAdmin) && (
                        <button
                            className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
                            onClick={() => setActiveTab('users')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '12px',
                                padding: '12px 16px', borderRadius: '12px',
                                background: activeTab === 'users' ? 'var(--bg-tertiary)' : 'transparent',
                                color: activeTab === 'users' ? 'var(--accent-primary)' : 'var(--text-main)',
                                border: 'none', cursor: 'pointer',
                                fontSize: '0.95rem', fontWeight: activeTab === 'users' ? '600' : '500',
                                width: '100%', textAlign: 'left',
                                transition: 'all 0.2s ease',
                                opacity: activeTab === 'users' ? 1 : 0.8
                            }}
                        >
                            <Users size={18} />
                            User Management
                        </button>
                    )}
                    {(isPlatformAdmin || isSuperAdmin) && (
                        <button
                            className={`nav-item ${activeTab === 'llm' ? 'active' : ''}`}
                            onClick={() => setActiveTab('llm')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '12px',
                                padding: '12px 16px', borderRadius: '12px',
                                background: activeTab === 'llm' ? 'var(--bg-tertiary)' : 'transparent',
                                color: activeTab === 'llm' ? 'var(--accent-primary)' : 'var(--text-main)',
                                border: 'none', cursor: 'pointer',
                                fontSize: '0.95rem', fontWeight: activeTab === 'llm' ? '600' : '500',
                                width: '100%', textAlign: 'left',
                                transition: 'all 0.2s ease',
                                opacity: activeTab === 'llm' ? 1 : 0.8
                            }}
                        >
                            <Cpu size={18} />
                            LLM Configuration
                        </button>
                    )}
                    <button
                        className={`nav-item ${activeTab === 'database' ? 'active' : ''}`}
                        onClick={() => setActiveTab('database')}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: '12px 16px', borderRadius: '12px',
                            background: activeTab === 'database' ? 'var(--bg-tertiary)' : 'transparent',
                            color: activeTab === 'database' ? 'var(--accent-primary)' : 'var(--text-main)',
                            border: 'none', cursor: 'pointer',
                            fontSize: '0.95rem', fontWeight: activeTab === 'database' ? '600' : '500',
                            width: '100%', textAlign: 'left',
                            transition: 'all 0.2s ease',
                            opacity: activeTab === 'database' ? 1 : 0.8
                        }}
                    >
                        <Database size={18} />
                        Database Connection
                    </button>
                    <button
                        className={`nav-item ${activeTab === 'governance' ? 'active' : ''}`}
                        onClick={() => setActiveTab('governance')}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: '12px 16px', borderRadius: '12px',
                            background: activeTab === 'governance' ? 'var(--bg-tertiary)' : 'transparent',
                            color: activeTab === 'governance' ? 'var(--accent-primary)' : 'var(--text-main)',
                            border: 'none', cursor: 'pointer',
                            fontSize: '0.95rem', fontWeight: activeTab === 'governance' ? '600' : '500',
                            width: '100%', textAlign: 'left',
                            transition: 'all 0.2s ease',
                            opacity: activeTab === 'governance' ? 1 : 0.8
                        }}
                    >
                        <Shield size={18} />
                        Data Governance
                    </button>
                    {isPlatformAdmin && (
                        <button
                            className={`nav-item ${activeTab === 'organizations' ? 'active' : ''}`}
                            onClick={() => setActiveTab('organizations')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '12px',
                                padding: '12px 16px', borderRadius: '12px',
                                background: activeTab === 'organizations' ? 'var(--bg-tertiary)' : 'transparent',
                                color: activeTab === 'organizations' ? 'var(--accent-primary)' : 'var(--text-main)',
                                border: 'none', cursor: 'pointer',
                                fontSize: '0.95rem', fontWeight: activeTab === 'organizations' ? '600' : '500',
                                width: '100%', textAlign: 'left',
                                transition: 'all 0.2s ease',
                                opacity: activeTab === 'organizations' ? 1 : 0.8
                            }}
                        >
                            <Building2 size={18} />
                            Organizations
                        </button>
                    )}
                    {/* Hide Workspaces for builder */}
                    {!(isBuilder && !isWorkspaceAdmin && !isSuperAdmin) && (
                        <button
                            className={`nav-item ${activeTab === 'workspaces' ? 'active' : ''}`}
                            onClick={() => setActiveTab('workspaces')}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '12px',
                                padding: '12px 16px', borderRadius: '12px',
                                background: activeTab === 'workspaces' ? 'var(--bg-tertiary)' : 'transparent',
                                color: activeTab === 'workspaces' ? 'var(--accent-primary)' : 'var(--text-main)',
                                border: 'none', cursor: 'pointer',
                                fontSize: '0.95rem', fontWeight: activeTab === 'workspaces' ? '600' : '500',
                                width: '100%', textAlign: 'left',
                                transition: 'all 0.2s ease',
                                opacity: activeTab === 'workspaces' ? 1 : 0.8
                            }}
                        >
                            <Layers size={18} />
                            Workspaces
                        </button>
                    )}
                </nav>
            </div>

            {/* Main Content */}
            <div className="admin-content">
                <div className="admin-header">
                    <h3 style={{ color: 'var(--text-main)' }}>
                        {activeTab === 'llm' && 'LLM Configuration'}
                        {activeTab === 'users' && 'User Management'}
                        {activeTab === 'database' && 'Database Connection'}
                        {activeTab === 'governance' && 'Content Governance'}
                        {activeTab === 'organizations' && 'Organizations'}
                        {activeTab === 'workspaces' && 'Workspaces'}
                    </h3>
                </div>

                <div className="admin-body custom-scrollbar">
                    {loading && <div className="loading-spinner-bar" />}

                    {error && <div className="alert-error">{error}</div>}
                    {successMsg && <div className="alert-success">{successMsg}</div>}

                    {/* LLM TAB */}
                    {activeTab === 'llm' && (
                        <div className="settings-section">
                            <p className="section-desc">Select and configure your preferred language model</p>

                            <div className="models-grid">
                                {MODELS.map(model => (
                                    <div
                                        key={model.id}
                                        className={`model-card ${llmConfig.model === model.id ? 'active' : ''}`}
                                        onClick={() => {
                                            setLlmConfig({ ...llmConfig, provider: model.provider, model: model.id });
                                        }}
                                    >
                                        <div className="model-header">
                                            <span className="model-name">{model.name}</span>
                                            {llmConfig.model === model.id && <Check size={16} className="text-blue-500" />}
                                        </div>
                                        <div className="model-provider">{(model.provider || 'AI').charAt(0).toUpperCase() + (model.provider || 'AI').slice(1)}</div>
                                        <p className="model-desc">{model.desc}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Custom Model Input */}
                            <div className="form-group mt-6">
                                <label>Custom Model ID (Optional)</label>
                                <p className="input-helper">Use a specific model string (e.g., gpt-5, gpt-4o, claude-3-opus) to override selection</p>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="e.g. gpt-4o"
                                    value={llmConfig.model}
                                    onChange={(e) => setLlmConfig({ ...llmConfig, model: e.target.value })}
                                />
                            </div>

                            <div className="form-group mt-6">
                                <label>API Key Configuration</label>
                                <p className="input-helper">Enter your API key for {(llmConfig?.provider || 'AI').charAt(0).toUpperCase() + (llmConfig?.provider || 'AI').slice(1)}</p>
                                <div className="input-row">
                                    <input
                                        type={showApiKey ? "text" : "password"}
                                        className="form-input"
                                        placeholder={`sk-...`}
                                        value={llmConfig.apiKey}
                                        onChange={(e) => setLlmConfig({ ...llmConfig, apiKey: e.target.value })}
                                    />
                                    <button
                                        onClick={() => setShowApiKey(!showApiKey)}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid var(--glass-border)',
                                            color: 'var(--text-main)',
                                            padding: '10px 16px',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontWeight: '500',
                                            display: 'flex', alignItems: 'center', gap: '8px',
                                            transition: 'all 0.2s ease',
                                            minWidth: '80px', justifyContent: 'center'
                                        }}
                                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                                        onMouseOut={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                                    >
                                        {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                                        {showApiKey ? 'Hide' : 'Show'}
                                    </button>
                                    <button className="btn-primary" onClick={() => handleSaveSettings('llm_config', llmConfig)}>
                                        <Save size={16} /> Save
                                    </button>
                                </div>
                                <div className="info-box">
                                    <Shield size={16} />
                                    <span>Your API key is stored securely and never shared</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* USERS TAB */}
                    {activeTab === 'users' && (
                        <div className="settings-section">
                            <div className="users-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                                <p className="section-desc mb-0">
                                    Manage users and their access permissions
                                    {user?.org_name ? ` for ${user.org_name}` : ''}
                                </p>

                                {isSuperAdmin && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '16px',
                                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(59, 130, 246, 0.05))',
                                        padding: '12px 20px',
                                        borderRadius: '16px',
                                        border: '1px solid rgba(139, 92, 246, 0.15)',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                        minWidth: '320px'
                                    }}>
                                        <div style={{
                                            background: 'rgba(139, 92, 246, 0.1)',
                                            padding: '8px',
                                            borderRadius: '10px',
                                            color: '#a78bfa'
                                        }}>
                                            <UserPlus size={20} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '0.65rem', color: 'var(--text-dim)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>
                                                Invite New Users
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {user?.invite_code ? (
                                                    <code style={{
                                                        fontSize: '1.1rem',
                                                        fontWeight: '800',
                                                        color: 'var(--text-main)',
                                                        letterSpacing: '2px',
                                                        fontFamily: "'JetBrains Mono', 'Fira Code', monospace"
                                                    }}>
                                                        {user.invite_code}
                                                    </code>
                                                ) : (
                                                    <span style={{ fontSize: '0.9rem', color: 'var(--text-dim)', fontStyle: 'italic' }}>
                                                        Refreshing code...
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {user?.invite_code && (
                                            <div style={{ display: 'flex', gap: '8px', marginLeft: '8px', borderLeft: '1px solid rgba(255, 255, 255, 0.1)', paddingLeft: '16px' }}>
                                                <button
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(user.invite_code);
                                                        showToast('Invite code copied to clipboard!', 'success');
                                                    }}
                                                    title="Copy to clipboard"
                                                    style={{
                                                        background: 'rgba(255, 255, 255, 0.05)',
                                                        border: '1px solid var(--glass-border)',
                                                        color: 'var(--text-main)',
                                                        padding: '8px 12px', borderRadius: '10px', cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', gap: '6px',
                                                        fontSize: '0.8rem', fontWeight: '600',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseOver={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                                                    onMouseOut={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                                                >
                                                    <Copy size={16} /> Copy
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        const confirmed = await showConfirm(`Regenerate invite code? Previous code will stop working for new signups.`);
                                                        if (confirmed) {
                                                            handleRegenerateInvite(user.org_id);
                                                        }
                                                    }}
                                                    title="Regenerate Code"
                                                    style={{
                                                        background: 'rgba(234, 179, 8, 0.1)',
                                                        border: '1px solid rgba(234, 179, 8, 0.2)',
                                                        color: '#eab308',
                                                        padding: '8px', borderRadius: '10px', cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', transition: 'all 0.2s'
                                                    }}
                                                    onMouseOver={e => e.currentTarget.style.background = 'rgba(234, 179, 8, 0.2)'}
                                                    onMouseOut={e => e.currentTarget.style.background = 'rgba(234, 179, 8, 0.1)'}
                                                >
                                                    <RefreshCw size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="glass-table-container">
                                <table className="users-table">
                                    <thead>
                                        <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                                            <th style={{ color: 'var(--text-dim)', fontWeight: '600', padding: '16px', textAlign: 'left' }}>NAME</th>
                                            <th style={{ color: 'var(--text-dim)', fontWeight: '600', padding: '16px', textAlign: 'left' }}>EMAIL</th>
                                            {user?.org_id === 'saturniq' && <th style={{ color: 'var(--text-dim)', fontWeight: '600', padding: '16px', textAlign: 'left' }}>ORG</th>}
                                            <th style={{ color: 'var(--text-dim)', fontWeight: '600', padding: '16px', textAlign: 'left' }}>ROLE</th>
                                            <th style={{ color: 'var(--text-dim)', fontWeight: '600', padding: '16px', textAlign: 'right' }}>ACTIONS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map(u => {
                                            const roleColors = {
                                                super_admin: { bg: 'rgba(239, 68, 68, 0.2)', text: '#fca5a5', border: 'rgba(239, 68, 68, 0.4)' },
                                                builder: { bg: 'rgba(59, 130, 246, 0.2)', text: '#93c5fd', border: 'rgba(59, 130, 246, 0.4)' },
                                                workspace_admin: { bg: 'rgba(139, 92, 246, 0.2)', text: '#c4b5fd', border: 'rgba(139, 92, 246, 0.4)' },
                                                viewer: { bg: 'rgba(16, 185, 129, 0.2)', text: '#6ee7b7', border: 'rgba(16, 185, 129, 0.4)' }
                                            };
                                            const currentStyle = roleColors[u.role] || roleColors['viewer'];
                                            const userWorkspaces = userWsMap[u.id] || [];

                                            return (
                                                <React.Fragment key={u.id}>
                                                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', transition: 'background 0.2s' }}>
                                                        <td style={{ padding: '16px', color: 'var(--text-main)', fontWeight: '500' }}>
                                                            {u.name}
                                                            {/* Workspace admin badges */}
                                                            {userWorkspaces.filter(w => w.ws_role === 'admin').length > 0 && (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                                                    {userWorkspaces.filter(w => w.ws_role === 'admin').map(w => (
                                                                        <span key={w.workspace_id} style={{
                                                                            background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.2)',
                                                                            color: '#8b5cf6', padding: '2px 8px', borderRadius: '12px',
                                                                            fontSize: '0.65rem', fontWeight: '600', display: 'inline-flex',
                                                                            alignItems: 'center', gap: '4px', cursor: 'pointer'
                                                                        }}
                                                                            onClick={() => removeWsAdmin(u.id, w.workspace_id, w.ws_name)}
                                                                            title={`Click to remove from ${w.ws_name}`}
                                                                        >
                                                                            <FolderOpen size={10} /> {w.ws_name} ✕
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '16px', color: 'var(--text-dim)', fontSize: '0.85rem' }}>{u.email}</td>
                                                        {user?.org_id === 'saturniq' && <td style={{ padding: '16px', color: 'var(--text-dim)', fontSize: '0.8rem' }}>{u.org_id || '-'}</td>}
                                                        <td style={{ padding: '16px' }}>
                                                            <select
                                                                value={u.role}
                                                                onChange={(e) => updateUserRole(u.id, e.target.value)}
                                                                disabled={u.id === user.id}
                                                                style={{
                                                                    background: currentStyle.bg,
                                                                    color: currentStyle.text,
                                                                    border: `1px solid ${currentStyle.border}`,
                                                                    padding: '6px 12px',
                                                                    borderRadius: '8px',
                                                                    outline: 'none',
                                                                    cursor: u.id === user.id ? 'not-allowed' : 'pointer',
                                                                    fontWeight: '600',
                                                                    fontSize: '0.85rem',
                                                                    appearance: 'none',
                                                                    textAlign: 'center'
                                                                }}
                                                            >
                                                                <option value="viewer" style={{ background: '#1e293b', color: '#cbd5e1' }}>Viewer</option>
                                                                <option value="builder" style={{ background: '#1e293b', color: '#cbd5e1' }}>Builder</option>
                                                                <option value="workspace_admin" style={{ background: '#1e293b', color: '#cbd5e1' }}>Workspace Admin</option>
                                                                <option value="super_admin" style={{ background: '#1e293b', color: '#cbd5e1' }}>Super Admin</option>
                                                            </select>
                                                        </td>
                                                        <td style={{ padding: '16px', textAlign: 'right' }}>
                                                            {u.id !== user.id && (
                                                                <button
                                                                    onClick={() => deleteUser(u.id)}
                                                                    title="Delete User"
                                                                    style={{
                                                                        background: 'rgba(239, 68, 68, 0.1)',
                                                                        border: '1px solid rgba(239, 68, 68, 0.2)',
                                                                        color: '#ef4444',
                                                                        padding: '8px',
                                                                        borderRadius: '8px',
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s ease',
                                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                                                                    }}
                                                                    onMouseOver={(e) => {
                                                                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                                                                        e.currentTarget.style.transform = 'scale(1.05)';
                                                                    }}
                                                                    onMouseOut={(e) => {
                                                                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                                                                        e.currentTarget.style.transform = 'scale(1)';
                                                                    }}
                                                                >
                                                                    <Trash2 size={18} />
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {/* Workspace picker row */}
                                                    {wsPickerUserId === u.id && (
                                                        <tr style={{ background: 'rgba(139, 92, 246, 0.03)' }}>
                                                            <td colSpan={user?.org_id === 'saturniq' ? 5 : 4} style={{ padding: '12px 16px' }}>
                                                                <div style={{ fontSize: '0.75rem', color: '#8b5cf6', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    Select workspace to assign as admin:
                                                                </div>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                                    {workspaces.length === 0 ? (
                                                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>No workspaces available</span>
                                                                    ) : workspaces.map(ws => {
                                                                        const alreadyAdmin = (userWsMap[u.id] || []).some(w => w.workspace_id === ws.id && w.ws_role === 'admin');
                                                                        return (
                                                                            <button
                                                                                key={ws.id}
                                                                                onClick={() => !alreadyAdmin && assignWsAdmin(u.id, ws.id)}
                                                                                disabled={alreadyAdmin}
                                                                                style={{
                                                                                    background: alreadyAdmin ? 'rgba(139, 92, 246, 0.15)' : 'rgba(59, 130, 246, 0.08)',
                                                                                    border: `1px solid ${alreadyAdmin ? 'rgba(139, 92, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
                                                                                    color: alreadyAdmin ? '#8b5cf6' : '#3b82f6',
                                                                                    padding: '6px 14px', borderRadius: '10px',
                                                                                    cursor: alreadyAdmin ? 'default' : 'pointer',
                                                                                    fontWeight: '600', fontSize: '0.75rem',
                                                                                    display: 'flex', alignItems: 'center', gap: '4px',
                                                                                    opacity: alreadyAdmin ? 0.6 : 1
                                                                                }}
                                                                            >
                                                                                <FolderOpen size={12} /> {ws.name} {alreadyAdmin ? '✓' : '+'}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                                <button onClick={() => setWsPickerUserId(null)} style={{
                                                                    marginTop: '8px', background: 'none', border: 'none',
                                                                    color: 'var(--text-muted)', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline'
                                                                }}>
                                                                    Close
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>


                        </div>
                    )}

                    {/* DATABASE TAB */}
                    {activeTab === 'database' && (
                        <div className="settings-section">
                            {/* === CONNECTED DATA SOURCES === */}
                            <div style={{ marginBottom: '32px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h3 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1.15rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Plug size={20} style={{ color: '#8b5cf6' }} /> Connected Data Sources
                                    </h3>
                                    <button
                                        className="btn-primary"
                                        onClick={() => setShowAddConnection(!showAddConnection)}
                                        style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', padding: '8px 16px' }}
                                    >
                                        <Plus size={16} /> Add Source
                                    </button>
                                </div>
                                <p className="section-desc" style={{ marginTop: 0 }}>Manage database connections, REST APIs, and cloud applications</p>

                                {/* Connection Cards */}
                                {dataConnections.length === 0 && !showAddConnection ? (
                                    <div style={{
                                        padding: '40px 24px', textAlign: 'center',
                                        border: '2px dashed rgba(139, 92, 246, 0.2)', borderRadius: '16px',
                                        background: 'rgba(139, 92, 246, 0.03)'
                                    }}>
                                        <Database size={40} style={{ color: 'rgba(139, 92, 246, 0.3)', marginBottom: '12px' }} />
                                        <p style={{ color: 'var(--text-dim)', fontSize: '0.95rem', margin: '0 0 16px' }}>No external data sources connected yet</p>
                                        <button className="btn-primary" onClick={() => setShowAddConnection(true)}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            <Plus size={16} /> Connect Your First Data Source
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gap: '12px' }}>
                                        {dataConnections.map(conn => (
                                            <div key={conn.id} style={{
                                                padding: '16px 20px',
                                                background: 'var(--bg-secondary)',
                                                border: '1px solid var(--glass-border)',
                                                borderRadius: '14px',
                                                display: 'flex', alignItems: 'center', gap: '16px',
                                                transition: 'all 0.2s ease'
                                            }}>
                                                <div style={{
                                                    width: '44px', height: '44px', borderRadius: '12px',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    background: conn.source_type === 'postgresql' ? 'rgba(59, 130, 246, 0.1)' :
                                                        conn.source_type === 'rest_api' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                                                    flexShrink: 0
                                                }}>
                                                    {conn.source_type === 'postgresql' ? <Database size={22} style={{ color: '#3b82f6' }} /> :
                                                        conn.source_type === 'rest_api' ? <Globe size={22} style={{ color: '#f59e0b' }} /> :
                                                            <Zap size={22} style={{ color: '#10b981' }} />}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                        <span style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '0.95rem' }}>{conn.name}</span>
                                                        <span style={{
                                                            padding: '2px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.5px',
                                                            background: conn.status === 'connected' ? 'rgba(16, 185, 129, 0.15)' :
                                                                conn.status === 'failed' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                                                            color: conn.status === 'connected' ? '#10b981' :
                                                                conn.status === 'failed' ? '#ef4444' : '#f59e0b'
                                                        }}>
                                                            {conn.status?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div style={{ color: 'var(--text-dim)', fontSize: '0.8rem', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                                        <span>{conn.source_type === 'postgresql' ? `${conn.host || ''}/${conn.database_name || ''}` : conn.base_url || conn.source_type}</span>
                                                        {conn.schema_cache && <span>{Object.keys(conn.schema_cache).length} tables</span>}
                                                        {conn.last_synced_at && <span>Synced: {new Date(conn.last_synced_at).toLocaleString()}</span>}
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                                    <button
                                                        onClick={() => handleSyncConnection(conn.id)}
                                                        disabled={syncingId === conn.id}
                                                        title="Sync Schema"
                                                        style={{
                                                            padding: '8px', borderRadius: '8px', border: '1px solid var(--glass-border)',
                                                            background: 'transparent', color: '#3b82f6', cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', opacity: syncingId === conn.id ? 0.5 : 1
                                                        }}
                                                    >
                                                        <RefreshCw size={16} className={syncingId === conn.id ? 'spinning' : ''} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteConnection(conn.id, conn.name)}
                                                        title="Delete Connection"
                                                        style={{
                                                            padding: '8px', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.2)',
                                                            background: 'transparent', color: '#ef4444', cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center'
                                                        }}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* === ADD NEW CONNECTION FORM === */}
                            {showAddConnection && (
                                <div style={{
                                    padding: '24px',
                                    background: 'rgba(139, 92, 246, 0.03)',
                                    border: '1px solid rgba(139, 92, 246, 0.2)',
                                    borderRadius: '16px',
                                    marginBottom: '24px'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h4 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1.05rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Zap size={18} style={{ color: '#f59e0b' }} /> New Data Source
                                        </h4>
                                        <button onClick={() => { setShowAddConnection(false); setConnTestResult(null); }} style={{
                                            background: 'transparent', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', padding: '4px'
                                        }}><X size={18} /></button>
                                    </div>

                                    <div className="db-form-grid">
                                        <div className="form-group">
                                            <label>Connection Name</label>
                                            <input type="text" className="form-input" placeholder="e.g. Sales Database" value={newConn.name} onChange={e => setNewConn({ ...newConn, name: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label>Source Type</label>
                                            <select className="form-select" value={newConn.source_type} onChange={e => { setNewConn({ ...newConn, source_type: e.target.value }); setConnTestResult(null); }}>
                                                <option value="postgresql">PostgreSQL Database</option>
                                                <option value="rest_api">REST API</option>
                                                <option value="cloud_app">Cloud Application</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* PostgreSQL Fields */}
                                    {newConn.source_type === 'postgresql' && (
                                        <div className="db-form-grid" style={{ marginTop: '16px' }}>
                                            <div className="form-group">
                                                <label>Host</label>
                                                <input type="text" className="form-input" placeholder="localhost or IP" value={newConn.host} onChange={e => setNewConn({ ...newConn, host: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Port</label>
                                                <input type="text" className="form-input" placeholder="5432" value={newConn.port} onChange={e => setNewConn({ ...newConn, port: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Database</label>
                                                <input type="text" className="form-input" placeholder="database_name" value={newConn.database} onChange={e => setNewConn({ ...newConn, database: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Username</label>
                                                <input type="text" className="form-input" placeholder="postgres" value={newConn.user} onChange={e => setNewConn({ ...newConn, user: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Password</label>
                                                <input type="password" className="form-input" placeholder="•••••••" value={newConn.password} onChange={e => setNewConn({ ...newConn, password: e.target.value })} />
                                            </div>
                                        </div>
                                    )}

                                    {/* REST API Fields */}
                                    {newConn.source_type === 'rest_api' && (
                                        <div style={{ marginTop: '16px' }}>
                                            <div className="db-form-grid">
                                                <div className="form-group">
                                                    <label>Base URL</label>
                                                    <input type="text" className="form-input" placeholder="https://api.example.com" value={newConn.base_url} onChange={e => setNewConn({ ...newConn, base_url: e.target.value })} />
                                                </div>
                                                <div className="form-group">
                                                    <label>Authentication</label>
                                                    <select className="form-select" value={newConn.auth_type} onChange={e => setNewConn({ ...newConn, auth_type: e.target.value })}>
                                                        <option value="none">No Auth</option>
                                                        <option value="bearer">Bearer Token</option>
                                                        <option value="api_key">API Key</option>
                                                    </select>
                                                </div>
                                                {newConn.auth_type === 'bearer' && (
                                                    <div className="form-group">
                                                        <label>Bearer Token</label>
                                                        <input type="password" className="form-input" placeholder="your-token" value={newConn.token} onChange={e => setNewConn({ ...newConn, token: e.target.value })} />
                                                    </div>
                                                )}
                                                {newConn.auth_type === 'api_key' && (
                                                    <>
                                                        <div className="form-group">
                                                            <label>Header Name</label>
                                                            <input type="text" className="form-input" placeholder="X-API-Key" value={newConn.api_key_header} onChange={e => setNewConn({ ...newConn, api_key_header: e.target.value })} />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>API Key Value</label>
                                                            <input type="password" className="form-input" placeholder="your-api-key" value={newConn.api_key_value} onChange={e => setNewConn({ ...newConn, api_key_value: e.target.value })} />
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {/* Endpoints */}
                                            <div style={{ marginTop: '16px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: 'var(--text-main)', fontWeight: '600', fontSize: '0.9rem' }}>
                                                    <Globe size={14} /> API Endpoints
                                                </label>
                                                {newConn.endpoints.map((ep, idx) => (
                                                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 80px 1fr auto', gap: '8px', marginBottom: '8px' }}>
                                                        <input type="text" className="form-input" placeholder="Table name" value={ep.name} onChange={e => {
                                                            const eps = [...newConn.endpoints]; eps[idx] = { ...eps[idx], name: e.target.value }; setNewConn({ ...newConn, endpoints: eps });
                                                        }} />
                                                        <input type="text" className="form-input" placeholder="/v1/endpoint" value={ep.path} onChange={e => {
                                                            const eps = [...newConn.endpoints]; eps[idx] = { ...eps[idx], path: e.target.value }; setNewConn({ ...newConn, endpoints: eps });
                                                        }} />
                                                        <select className="form-select" value={ep.method} onChange={e => {
                                                            const eps = [...newConn.endpoints]; eps[idx] = { ...eps[idx], method: e.target.value }; setNewConn({ ...newConn, endpoints: eps });
                                                        }}>
                                                            <option value="GET">GET</option>
                                                            <option value="POST">POST</option>
                                                        </select>
                                                        <input type="text" className="form-input" placeholder="data.records" value={ep.response_path} onChange={e => {
                                                            const eps = [...newConn.endpoints]; eps[idx] = { ...eps[idx], response_path: e.target.value }; setNewConn({ ...newConn, endpoints: eps });
                                                        }} />
                                                        {newConn.endpoints.length > 1 && (
                                                            <button onClick={() => { const eps = newConn.endpoints.filter((_, i) => i !== idx); setNewConn({ ...newConn, endpoints: eps }); }}
                                                                style={{ padding: '6px', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}>
                                                                <Trash2 size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                                <button onClick={() => setNewConn({ ...newConn, endpoints: [...newConn.endpoints, { name: '', path: '', method: 'GET', response_path: '' }] })}
                                                    style={{ padding: '6px 12px', fontSize: '0.8rem', background: 'transparent', border: '1px dashed var(--glass-border)', borderRadius: '8px', color: 'var(--text-dim)', cursor: 'pointer' }}>
                                                    + Add Endpoint
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Cloud App Fields */}
                                    {newConn.source_type === 'cloud_app' && (
                                        <div className="db-form-grid" style={{ marginTop: '16px' }}>
                                            <div className="form-group">
                                                <label>Cloud Platform</label>
                                                <select className="form-select" value={newConn.cloud_type} onChange={e => setNewConn({ ...newConn, cloud_type: e.target.value })}>
                                                    <option value="servicenow">ServiceNow</option>
                                                    <option value="salesforce">Salesforce</option>
                                                    <option value="dynamics365">Microsoft Dynamics 365</option>
                                                    <option value="hubspot">HubSpot</option>
                                                    <option value="zoho">Zoho CRM</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Instance URL</label>
                                                <input type="text" className="form-input" placeholder="https://mycompany.service-now.com" value={newConn.instance_url} onChange={e => setNewConn({ ...newConn, instance_url: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Client ID</label>
                                                <input type="text" className="form-input" placeholder="OAuth Client ID" value={newConn.client_id} onChange={e => setNewConn({ ...newConn, client_id: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Client Secret / API Token</label>
                                                <input type="password" className="form-input" placeholder="•••••••" value={newConn.client_secret} onChange={e => setNewConn({ ...newConn, client_secret: e.target.value })} />
                                            </div>
                                        </div>
                                    )}

                                    {/* Test Result */}
                                    {connTestResult && (
                                        <div className={`status-message ${connTestResult.success ? 'success' : 'error'}`} style={{ marginTop: '12px' }}>
                                            {connTestResult.success ? <Check size={18} /> : <Activity size={18} />}
                                            {connTestResult.message}
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="actions-row mt-6" style={{ marginTop: '16px' }}>
                                        <button className="btn-secondary dark" onClick={handleTestNewConnection} disabled={loading}>
                                            <Activity size={16} /> {loading ? 'Testing...' : 'Test Connection'}
                                        </button>
                                        <button className="btn-primary" onClick={handleSaveConnection} disabled={loading}>
                                            <Save size={16} /> {loading ? 'Saving...' : 'Save & Connect'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* === MAIN DATABASE CONFIG (Collapsible) === */}
                            <div style={{ marginTop: '8px' }}>
                                <button
                                    onClick={() => setShowMainDbForm(!showMainDbForm)}
                                    style={{
                                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '14px 20px', background: 'var(--bg-secondary)',
                                        border: '1px solid var(--glass-border)', borderRadius: '14px',
                                        cursor: 'pointer', color: 'var(--text-main)', fontWeight: '600', fontSize: '0.95rem'
                                    }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Server size={18} style={{ color: '#3b82f6' }} /> Main Application Database
                                    </span>
                                    {showMainDbForm ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </button>

                                {showMainDbForm && (
                                    <div style={{ padding: '20px', border: '1px solid var(--glass-border)', borderTop: 'none', borderRadius: '0 0 14px 14px', background: 'var(--bg-secondary)' }}>
                                        <p className="section-desc" style={{ marginTop: 0 }}>Configure the primary application database connection</p>
                                        <div className="db-form-grid">
                                            <div className="form-group">
                                                <label>Database Type</label>
                                                <select className="form-select" value={dbConfig.type} disabled>
                                                    <option>PostgreSQL</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Host</label>
                                                <input type="text" className="form-input" value={dbConfig.host} onChange={e => setDbConfig({ ...dbConfig, host: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Port</label>
                                                <input type="text" className="form-input" value={dbConfig.port} onChange={e => setDbConfig({ ...dbConfig, port: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Database Name</label>
                                                <input type="text" className="form-input" value={dbConfig.database} onChange={e => setDbConfig({ ...dbConfig, database: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Username</label>
                                                <input type="text" className="form-input" value={dbConfig.username} onChange={e => setDbConfig({ ...dbConfig, username: e.target.value })} />
                                            </div>
                                            <div className="form-group">
                                                <label>Password</label>
                                                <input type="password" className="form-input" value={dbConfig.password} onChange={e => setDbConfig({ ...dbConfig, password: e.target.value })} placeholder="•••••••" />
                                            </div>
                                        </div>

                                        {dbTestResult && (
                                            <div className={`status-message ${dbTestResult.success ? 'success' : 'error'}`}>
                                                {dbTestResult.success ? <Check size={18} /> : <Activity size={18} />}
                                                {dbTestResult.message}
                                            </div>
                                        )}

                                        <div className="actions-row mt-6">
                                            <button className="btn-secondary dark" onClick={handleTestDb}>
                                                <Activity size={16} /> Test Connection
                                            </button>
                                            <button className="btn-primary" onClick={handleSaveDbConfig}>
                                                <Save size={16} /> Save Configuration
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Excel Upload Section */}
                            <div className="info-box" style={{ marginTop: '24px', background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <FileUp size={20} style={{ color: '#60a5fa' }} />
                                        <div>
                                            <h4 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1rem' }}>Excel / CSV Upload</h4>
                                            <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: '0.85rem' }}>Upload spreadsheets to create new data tables automatically.</p>
                                        </div>
                                    </div>

                                    {/* connection selector */}
                                    <div style={{ marginTop: '12px', marginBottom: '8px' }}>
                                        <label style={{ display: 'block', marginBottom: '6px', color: 'var(--text-dim)', fontSize: '0.85rem' }}>Target Database</label>
                                        <select
                                            className="form-select"
                                            value={uploadConnectionId}
                                            onChange={e => setUploadConnectionId(e.target.value)}
                                            style={{ maxWidth: '300px', fontSize: '0.9rem', padding: '8px 12px' }}
                                        >
                                            <option value="main">Main Application Database</option>
                                            {dataConnections.filter(c => c.source_type === 'postgresql').map(c => (
                                                <option key={c.id} value={c.id}>{c.name} ({c.connection_config?.host})</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div style={{ marginTop: '8px' }}>
                                        <label className="btn-primary" style={{
                                            display: 'inline-flex', cursor: uploadProgress.active ? 'not-allowed' : 'pointer', gap: '8px',
                                            opacity: uploadProgress.active ? 0.5 : 1, pointerEvents: uploadProgress.active ? 'none' : 'auto'
                                        }}>
                                            <FileUp size={16} />
                                            <span>{uploadProgress.active ? 'Uploading...' : 'Select Excel Files'}</span>
                                            <input type="file" multiple accept=".xlsx, .xls, .csv" onChange={handleFileUpload} style={{ display: 'none' }} disabled={uploadProgress.active} />
                                        </label>
                                    </div>

                                    {/* Upload Progress Bar */}
                                    {uploadProgress.active && (
                                        <div style={{ marginTop: '12px', width: '100%' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                <span style={{ color: 'var(--text-main)', fontSize: '0.85rem', fontWeight: '500' }}>{uploadProgress.fileName}</span>
                                                <span style={{ color: '#60a5fa', fontSize: '0.85rem', fontWeight: '700' }}>{uploadProgress.percent}%</span>
                                            </div>
                                            <div style={{
                                                width: '100%', height: '8px', borderRadius: '8px',
                                                background: 'rgba(255,255,255,0.08)', overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    width: `${uploadProgress.percent}%`, height: '100%',
                                                    borderRadius: '8px',
                                                    background: uploadProgress.percent === 100 ? 'linear-gradient(90deg, #22c55e, #4ade80)' : 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                                                    transition: 'width 0.3s ease-in-out'
                                                }} />
                                            </div>
                                            <p style={{ margin: '6px 0 0', color: 'var(--text-dim)', fontSize: '0.8rem' }}>{uploadProgress.status}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* GOVERNANCE TAB */}
                    {activeTab === 'governance' && (
                        <div className="settings-section" style={{ maxWidth: '100%' }}>
                            <div className="users-actions" style={{ marginBottom: '24px' }}>
                                <p className="section-desc mb-0">Manage data visibility and access across the organization</p>
                            </div>

                            {/* Governance Sub-Filters */}
                            <div className="gov-filters" style={{
                                display: 'flex', gap: '12px', marginBottom: '24px',
                                background: 'rgba(255,255,255,0.03)', padding: '6px', borderRadius: '14px',
                                width: 'fit-content', border: '1px solid var(--glass-border)'
                            }}>
                                {['all', 'table', 'chart', 'dashboard'].map(type => (
                                    <button
                                        key={type}
                                        onClick={() => setGovFilter(type)}
                                        style={{
                                            padding: '8px 20px',
                                            borderRadius: '10px',
                                            background: govFilter === type ? 'var(--accent-primary)' : 'transparent',
                                            color: govFilter === type ? 'white' : 'var(--text-dim)',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            fontSize: '0.85rem',
                                            transition: 'all 0.2s ease',
                                            boxShadow: govFilter === type ? '0 4px 12px rgba(139, 92, 246, 0.2)' : 'none'
                                        }}
                                    >
                                        {type === 'all' ? 'All Assets' : type.charAt(0).toUpperCase() + type.slice(1) + 's'}
                                    </button>
                                ))}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                                {selectedAssets.length > 0 && (
                                    <button
                                        onClick={() => {
                                            setSelectedAsset({ id: 'bulk', asset_name: `${selectedAssets.length} Assets Selected`, isBulk: true, assetIds: selectedAssets });
                                            setShowAccessModal(true);
                                        }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '8px',
                                            padding: '10px 20px', borderRadius: '12px',
                                            background: 'var(--accent-primary)', color: 'white',
                                            border: 'none', cursor: 'pointer',
                                            fontSize: '0.9rem', fontWeight: '700', transition: 'all 0.2s',
                                            boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)'
                                        }}
                                        onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                        onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                                    >
                                        <Lock size={16} />
                                        <span>Bulk Manage Access ({selectedAssets.length})</span>
                                    </button>
                                )}
                                <button
                                    onClick={() => fetchAssets(true)}
                                    disabled={loading}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        padding: '8px 16px', borderRadius: '10px',
                                        background: 'rgba(255,255,255,0.05)', color: 'var(--text-main)',
                                        border: '1px solid var(--glass-border)', cursor: 'pointer',
                                        fontSize: '0.85rem', fontWeight: '600', transition: 'all 0.2'
                                    }}
                                    onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                    onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                >
                                    {loading ? <Activity size={14} className="spin" /> : <Activity size={14} />}
                                    <span>{loading ? 'Syncing...' : 'Sync Assets'}</span>
                                </button>
                            </div>

                            <div className="glass-table-container" style={{ width: '100%', borderRadius: '12px', overflowX: 'auto' }}>
                                <table className="users-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                                            <th style={{ padding: '16px', textAlign: 'left', width: '40px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedAssets.length > 0 && selectedAssets.length === assets.filter(a => {
                                                        if (isBuilder && !isWorkspaceAdmin && !isSuperAdmin) {
                                                            if (a.owner_email !== user?.email) return false;
                                                        }
                                                        if (govFilter === 'all') return true;
                                                        if (govFilter === 'table') return a.asset_type === 'table' || a.asset_type === 'view';
                                                        return a.asset_type === govFilter;
                                                    }).length}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            const filteredIds = assets.filter(a => {
                                                                if (isBuilder && !isWorkspaceAdmin && !isSuperAdmin) {
                                                                    if (a.owner_email !== user?.email) return false;
                                                                }
                                                                if (govFilter === 'all') return true;
                                                                if (govFilter === 'table') return a.asset_type === 'table' || a.asset_type === 'view';
                                                                return a.asset_type === govFilter;
                                                            }).map(a => a.id);
                                                            setSelectedAssets(filteredIds);
                                                        } else {
                                                            setSelectedAssets([]);
                                                        }
                                                    }}
                                                    style={{ cursor: 'pointer', accentColor: 'var(--accent-primary)', width: '16px', height: '16px' }}
                                                />
                                            </th>
                                            <th style={{ color: 'var(--text-dim)', fontWeight: '600', padding: '16px', textAlign: 'left' }}>ASSET NAME</th>
                                            <th style={{ color: 'var(--text-dim)', fontWeight: '600', padding: '16px', textAlign: 'left', width: '100px' }}>TYPE</th>
                                            <th style={{ color: 'var(--text-dim)', fontWeight: '600', padding: '16px', textAlign: 'left' }}>OWNER</th>
                                            <th style={{ color: 'var(--text-dim)', fontWeight: '600', padding: '16px', textAlign: 'left' }}>WORKSPACES</th>
                                            <th style={{ color: 'var(--text-dim)', fontWeight: '600', padding: '16px', textAlign: 'left' }}>ORG</th>
                                            <th style={{ color: 'var(--text-dim)', fontWeight: '600', padding: '16px', textAlign: 'right' }}>ACTIONS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {assets.filter(a => {
                                            // Builder only sees their own data
                                            if (isBuilder && !isWorkspaceAdmin && !isSuperAdmin) {
                                                if (a.owner_email !== user?.email) return false;
                                            }
                                            if (govFilter === 'all') return true;
                                            if (govFilter === 'table') return a.asset_type === 'table' || a.asset_type === 'view';
                                            return a.asset_type === govFilter;
                                        }).length === 0 ? (
                                            <tr>
                                                <td colSpan="8" style={{ padding: '48px', textAlign: 'center', color: 'var(--text-dim)' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                                        <Shield size={32} opacity={0.3} />
                                                        <span>No {govFilter !== 'all' ? govFilter + 's' : 'assets'} found in this category.</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : assets
                                            .filter(a => {
                                                // Builder only sees their own data
                                                if (isBuilder && !isWorkspaceAdmin && !isSuperAdmin) {
                                                    if (a.owner_email !== user?.email) return false;
                                                }
                                                if (govFilter === 'all') return true;
                                                if (govFilter === 'table') return a.asset_type === 'table' || a.asset_type === 'view';
                                                return a.asset_type === govFilter;
                                            })
                                            .map(asset => (
                                                <tr key={asset.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', transition: 'background 0.2s', background: selectedAssets.includes(asset.id) ? 'rgba(139, 92, 246, 0.05)' : 'transparent' }}>
                                                    <td style={{ padding: '16px', textAlign: 'left' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedAssets.includes(asset.id)}
                                                            onChange={() => {
                                                                if (selectedAssets.includes(asset.id)) {
                                                                    setSelectedAssets(selectedAssets.filter(id => id !== asset.id));
                                                                } else {
                                                                    setSelectedAssets([...selectedAssets, asset.id]);
                                                                }
                                                            }}
                                                            style={{ cursor: 'pointer', accentColor: 'var(--accent-primary)', width: '16px', height: '16px' }}
                                                        />
                                                    </td>
                                                    <td style={{
                                                        padding: '16px',
                                                        color: 'var(--text-main)',
                                                        fontWeight: '500',
                                                        maxWidth: '200px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }} title={asset.asset_name}>
                                                        {asset.asset_name}
                                                    </td>
                                                    <td style={{ padding: '16px' }}>
                                                        <span style={{
                                                            padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700',
                                                            display: 'inline-block',
                                                            background: asset.asset_type === 'view' ? 'rgba(139, 92, 246, 0.1)' :
                                                                asset.asset_type === 'chart' ? 'rgba(6, 182, 212, 0.1)' :
                                                                    asset.asset_type === 'dashboard' ? 'rgba(236, 72, 153, 0.1)' :
                                                                        'rgba(59, 130, 246, 0.1)',
                                                            color: asset.asset_type === 'view' ? '#a78bfa' :
                                                                asset.asset_type === 'chart' ? '#22d3ee' :
                                                                    asset.asset_type === 'dashboard' ? '#f472b6' :
                                                                        '#60a5fa',
                                                            border: `1px solid ${asset.asset_type === 'view' ? 'rgba(139, 92, 246, 0.2)' :
                                                                asset.asset_type === 'chart' ? 'rgba(6, 182, 212, 0.2)' :
                                                                    asset.asset_type === 'dashboard' ? 'rgba(236, 72, 153, 0.2)' :
                                                                        'rgba(59, 130, 246, 0.2)'
                                                                }`
                                                        }}>
                                                            {asset.asset_type.toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '16px' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                            <span style={{ color: 'var(--text-main)', fontSize: '0.9rem' }}>{asset.owner_name}</span>
                                                            <span style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>{asset.owner_email}</span>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '16px' }}>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                            {asset.workspaces ? asset.workspaces.split(', ').map(ws => (
                                                                <span key={ws} style={{
                                                                    padding: '2px 8px', borderRadius: '6px',
                                                                    background: 'rgba(139, 92, 246, 0.1)',
                                                                    color: 'var(--accent-primary)',
                                                                    fontSize: '0.75rem', fontWeight: '600',
                                                                    border: '1px solid rgba(139, 92, 246, 0.2)'
                                                                }}>
                                                                    {ws}
                                                                </span>
                                                            )) : <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>Personal</span>}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '16px', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                                                        <span style={{
                                                            padding: '2px 6px', borderRadius: '4px', border: '1px solid var(--glass-border)',
                                                            background: 'rgba(255,255,255,0.03)'
                                                        }}>
                                                            {asset.org_id}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '16px', textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', minWidth: '80px' }}>
                                                            {/* {(isBuilder || isWorkspaceAdmin) && asset.asset_type === 'dashboard' && (
                                                                <button
                                                                    onClick={() => {
                                                                        setReviewDashboard(asset);
                                                                        setShowReviewModal(true);
                                                                    }}
                                                                    title="Submit for Review"
                                                                    style={{
                                                                        background: 'rgba(234, 179, 8, 0.1)',
                                                                        border: '1px solid rgba(234, 179, 8, 0.2)',
                                                                        color: '#eab308',
                                                                        padding: '6px',
                                                                        borderRadius: '8px',
                                                                        cursor: 'pointer',
                                                                        display: 'inline-flex', alignItems: 'center',
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                >
                                                                    <Send size={16} />
                                                                </button>
                                                            )} */}
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedAsset(asset);
                                                                    setShowAccessModal(true);
                                                                }}
                                                                title="Manage Access"
                                                                style={{
                                                                    background: 'rgba(139, 92, 246, 0.1)',
                                                                    border: '1px solid rgba(139, 92, 246, 0.2)',
                                                                    color: '#a78bfa',
                                                                    padding: '6px',
                                                                    borderRadius: '8px',
                                                                    cursor: 'pointer',
                                                                    display: 'inline-flex', alignItems: 'center',
                                                                    transition: 'all 0.2s'
                                                                }}
                                                                onMouseOver={e => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)'}
                                                                onMouseOut={e => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)'}
                                                            >
                                                                <Key size={16} />
                                                            </button>
                                                            {/* Hide table delete for builder, show for workspace_admin and super_admin */}
                                                            {!(isBuilder && !isWorkspaceAdmin && !isSuperAdmin && (asset.asset_type === 'table' || asset.asset_type === 'view')) && (
                                                                <button
                                                                    onClick={() => handleDeleteAsset(asset)}
                                                                    title={`Delete ${asset.asset_type}`}
                                                                    style={{
                                                                        background: 'rgba(239, 68, 68, 0.1)',
                                                                        border: '1px solid rgba(239, 68, 68, 0.2)',
                                                                        color: '#ef4444',
                                                                        padding: '6px',
                                                                        borderRadius: '8px',
                                                                        cursor: 'pointer',
                                                                        display: 'inline-flex', alignItems: 'center',
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                    onMouseOver={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
                                                                    onMouseOut={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* ORGANIZATIONS TAB */}
                    {activeTab === 'organizations' && (
                        <div className="settings-section">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                                <p className="section-desc mb-0">Manage client organizations and their access limits</p>
                                <button
                                    onClick={() => setShowCreateOrg(!showCreateOrg)}
                                    style={{
                                        background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                                        border: 'none', color: 'white', padding: '10px 20px',
                                        borderRadius: '10px', cursor: 'pointer', fontWeight: '600',
                                        fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px',
                                        transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
                                    }}
                                >
                                    <Plus size={16} />
                                    New Organization
                                </button>
                            </div>

                            {/* Create Org Form */}
                            {showCreateOrg && (
                                <div style={{
                                    background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)',
                                    borderRadius: '16px', padding: '24px', marginBottom: '24px',
                                    animation: 'fadeIn 0.3s ease'
                                }}>
                                    <h4 style={{ color: 'var(--text-main)', marginBottom: '20px', fontSize: '1.05rem', fontWeight: '600' }}>
                                        Create New Organization
                                    </h4>

                                    {/* Org Details */}
                                    <div style={{ marginBottom: '20px' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--accent-primary)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Building2 size={14} /> Organization Details
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '6px', fontWeight: '600' }}>Organization Name *</label>
                                                <input
                                                    type="text"
                                                    className="form-input"
                                                    placeholder="e.g. Microsoft India"
                                                    value={newOrgName}
                                                    onChange={(e) => setNewOrgName(e.target.value)}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '6px', fontWeight: '600' }}>Plan</label>
                                                <select
                                                    className="form-input"
                                                    value={newOrgPlan}
                                                    onChange={(e) => setNewOrgPlan(e.target.value)}
                                                    style={{ width: '100%' }}
                                                >
                                                    <option value="starter">Starter</option>
                                                    <option value="professional">Professional</option>
                                                    <option value="enterprise">Enterprise</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '6px', fontWeight: '600' }}>Max Workspaces</label>
                                                <input
                                                    type="number"
                                                    className="form-input"
                                                    value={newOrgMaxWS}
                                                    onChange={(e) => setNewOrgMaxWS(parseInt(e.target.value) || 3)}
                                                    min="1" max="100"
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '6px', fontWeight: '600' }}>Max Users</label>
                                                <input
                                                    type="number"
                                                    className="form-input"
                                                    value={newOrgMaxUsers}
                                                    onChange={(e) => setNewOrgMaxUsers(parseInt(e.target.value) || 50)}
                                                    min="1" max="10000"
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div style={{ borderTop: '1px solid var(--glass-border)', margin: '20px 0' }} />

                                    {/* First Admin User */}
                                    <div style={{ marginBottom: '20px' }}>
                                        <div style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Users size={14} /> First Admin User (Super Admin)
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '6px', fontWeight: '600' }}>Admin Name</label>
                                                <input
                                                    type="text"
                                                    className="form-input"
                                                    placeholder="e.g. Anmol Sharma"
                                                    value={newOrgAdminName}
                                                    onChange={(e) => setNewOrgAdminName(e.target.value)}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '6px', fontWeight: '600' }}>Admin Email *</label>
                                                <input
                                                    type="email"
                                                    className="form-input"
                                                    placeholder="admin@microsoft.com"
                                                    value={newOrgAdminEmail}
                                                    onChange={(e) => setNewOrgAdminEmail(e.target.value)}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                            <div style={{ gridColumn: '1 / -1' }}>
                                                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '6px', fontWeight: '600' }}>Admin Password *</label>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <input
                                                        type={showAdminPassword ? 'text' : 'password'}
                                                        className="form-input"
                                                        placeholder="Min 6 characters"
                                                        value={newOrgAdminPassword}
                                                        onChange={(e) => setNewOrgAdminPassword(e.target.value)}
                                                        style={{ flex: 1 }}
                                                    />
                                                    <button
                                                        onClick={() => setShowAdminPassword(!showAdminPassword)}
                                                        type="button"
                                                        style={{
                                                            background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)',
                                                            color: 'var(--text-dim)', padding: '8px 14px', borderRadius: '8px',
                                                            cursor: 'pointer', display: 'flex', alignItems: 'center'
                                                        }}
                                                    >
                                                        {showAdminPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ marginTop: '8px', fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Shield size={12} />
                                            This user will be the Super Admin of the new organization and can manage all settings, users, and workspaces.
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => setShowCreateOrg(false)}
                                            style={{
                                                background: 'transparent', border: '1px solid var(--glass-border)',
                                                color: 'var(--text-dim)', padding: '10px 20px',
                                                borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleCreateOrg}
                                            disabled={loading || !newOrgName.trim() || !newOrgAdminEmail.trim() || newOrgAdminPassword.length < 6}
                                            style={{
                                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                                border: 'none', color: 'white', padding: '10px 24px',
                                                borderRadius: '10px', cursor: 'pointer', fontWeight: '600',
                                                fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px',
                                                opacity: (!newOrgName.trim() || !newOrgAdminEmail.trim() || newOrgAdminPassword.length < 6 || loading) ? 0.5 : 1
                                            }}
                                        >
                                            <Check size={16} />
                                            Create Organization & Admin
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Organizations Grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '16px' }}>
                                {organizations.map(org => {
                                    const planColors = {
                                        starter: { bg: 'rgba(234, 179, 8, 0.1)', text: '#eab308', border: 'rgba(234, 179, 8, 0.2)' },
                                        professional: { bg: 'rgba(59, 130, 246, 0.1)', text: '#3b82f6', border: 'rgba(59, 130, 246, 0.2)' },
                                        enterprise: { bg: 'rgba(139, 92, 246, 0.1)', text: '#8b5cf6', border: 'rgba(139, 92, 246, 0.2)' }
                                    };
                                    const pc = planColors[org.plan] || planColors.starter;

                                    return (
                                        <div key={org.id} style={{
                                            background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)',
                                            borderRadius: '16px', padding: '20px',
                                            transition: 'all 0.2s', position: 'relative',
                                            opacity: org.is_active ? 1 : 0.6
                                        }}>
                                            {/* Header */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                                {editingOrg?.id === org.id ? (
                                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                        <input
                                                            value={editingOrg.name}
                                                            onChange={e => setEditingOrg({ ...editingOrg, name: e.target.value })}
                                                            style={{
                                                                background: 'var(--bg-secondary)', border: '1px solid var(--glass-border)',
                                                                color: 'var(--text-main)', padding: '6px 10px', borderRadius: '8px',
                                                                fontSize: '0.9rem', width: '100%'
                                                            }}
                                                            placeholder="Org Name"
                                                        />
                                                        <input
                                                            value={editingOrg.new_id !== undefined ? editingOrg.new_id : editingOrg.id}
                                                            onChange={e => setEditingOrg({ ...editingOrg, new_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                                                            style={{
                                                                background: 'var(--bg-secondary)', border: '1px solid var(--glass-border)',
                                                                color: 'var(--text-main)', padding: '6px 10px', borderRadius: '8px',
                                                                fontSize: '0.8rem', width: '100%'
                                                            }}
                                                            placeholder="Org ID (e.g. org_name)"
                                                        />
                                                        <select
                                                            value={editingOrg.plan}
                                                            onChange={e => setEditingOrg({ ...editingOrg, plan: e.target.value })}
                                                            style={{
                                                                background: 'var(--bg-secondary)', border: '1px solid var(--glass-border)',
                                                                color: 'var(--text-main)', padding: '6px 10px', borderRadius: '8px',
                                                                fontSize: '0.9rem'
                                                            }}
                                                        >
                                                            <option value="starter">Starter</option>
                                                            <option value="professional">Professional</option>
                                                            <option value="enterprise">Enterprise</option>
                                                        </select>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                                                            <Building2 size={18} style={{ color: 'var(--accent-primary)' }} />
                                                            <h4 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1.05rem', fontWeight: '700' }}>
                                                                {org.name}
                                                            </h4>
                                                        </div>
                                                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{org.id}</span>
                                                    </div>
                                                )}
                                                {editingOrg?.id !== org.id && (
                                                    <span style={{
                                                        background: pc.bg, color: pc.text, border: `1px solid ${pc.border}`,
                                                        padding: '3px 10px', borderRadius: '8px', fontSize: '0.7rem',
                                                        fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px'
                                                    }}>
                                                        {org.plan}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Stats */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                                                <div style={{
                                                    background: 'rgba(59, 130, 246, 0.06)', borderRadius: '10px', padding: '10px 14px',
                                                    border: '1px solid rgba(59, 130, 246, 0.1)'
                                                }}>
                                                    {editingOrg?.id === org.id ? (
                                                        <div>
                                                            <div style={{ fontSize: '0.65rem', color: 'var(--text-dim)', marginBottom: '4px' }}>Max Users</div>
                                                            <input
                                                                type="number"
                                                                value={editingOrg.max_users}
                                                                onChange={e => setEditingOrg({ ...editingOrg, max_users: e.target.value })}
                                                                style={{
                                                                    background: 'transparent', border: 'none', borderBottom: '1px solid #3b82f6',
                                                                    color: '#3b82f6', fontSize: '1.1rem', fontWeight: '700', width: '60px'
                                                                }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#3b82f6' }}>
                                                                {org.user_count || '0'}
                                                                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: '400', marginLeft: '2px' }}>/ {org.max_users}</span>
                                                            </div>
                                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', fontWeight: '500' }}>Users</div>
                                                        </>
                                                    )}
                                                </div>
                                                <div style={{
                                                    background: 'rgba(16, 185, 129, 0.06)', borderRadius: '10px', padding: '10px 14px',
                                                    border: '1px solid rgba(16, 185, 129, 0.1)'
                                                }}>
                                                    {editingOrg?.id === org.id ? (
                                                        <div>
                                                            <div style={{ fontSize: '0.65rem', color: 'var(--text-dim)', marginBottom: '4px' }}>Max Workspaces</div>
                                                            <input
                                                                type="number"
                                                                value={editingOrg.max_workspaces}
                                                                onChange={e => setEditingOrg({ ...editingOrg, max_workspaces: e.target.value })}
                                                                style={{
                                                                    background: 'transparent', border: 'none', borderBottom: '1px solid #10b981',
                                                                    color: '#10b981', fontSize: '1.1rem', fontWeight: '700', width: '60px'
                                                                }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#10b981' }}>
                                                                {org.workspace_count || '0'}
                                                                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: '400', marginLeft: '2px' }}>/ {org.max_workspaces}</span>
                                                            </div>
                                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', fontWeight: '500' }}>Workspaces</div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Invite Code */}
                                            <div style={{
                                                background: 'var(--bg-secondary)', borderRadius: '10px', padding: '10px 14px',
                                                border: '1px solid var(--glass-border)', marginBottom: '12px',
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                                            }}>
                                                <div>
                                                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: '600', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        INVITE CODE
                                                    </div>
                                                    <code style={{
                                                        fontSize: '0.9rem', fontWeight: '700', color: 'var(--accent-primary)',
                                                        letterSpacing: '1.5px', fontFamily: 'monospace'
                                                    }}>
                                                        {org.invite_code || '—'}
                                                    </code>
                                                </div>
                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                    <button
                                                        onClick={() => copyInviteCode(org.invite_code)}
                                                        title="Copy invite code"
                                                        style={{
                                                            background: copiedCode === org.invite_code ? 'rgba(16, 185, 129, 0.15)' : 'rgba(139, 92, 246, 0.1)',
                                                            border: '1px solid ' + (copiedCode === org.invite_code ? 'rgba(16, 185, 129, 0.3)' : 'rgba(139, 92, 246, 0.2)'),
                                                            color: copiedCode === org.invite_code ? '#10b981' : '#a78bfa',
                                                            padding: '6px', borderRadius: '8px', cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        {copiedCode === org.invite_code ? <Check size={14} /> : <Copy size={14} />}
                                                    </button>
                                                    <button
                                                        onClick={() => handleRegenerateInvite(org.id)}
                                                        title="Regenerate invite code"
                                                        style={{
                                                            background: 'rgba(234, 179, 8, 0.1)',
                                                            border: '1px solid rgba(234, 179, 8, 0.2)',
                                                            color: '#eab308',
                                                            padding: '6px', borderRadius: '8px', cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        <RefreshCw size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                                {editingOrg?.id === org.id ? (
                                                    <>
                                                        <button
                                                            onClick={() => setEditingOrg(null)}
                                                            style={{
                                                                background: 'transparent', border: '1px solid var(--glass-border)',
                                                                color: 'var(--text-dim)', padding: '6px 14px', borderRadius: '8px',
                                                                fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer'
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={handleUpdateOrg}
                                                            style={{
                                                                background: '#8b5cf6', border: 'none',
                                                                color: 'white', padding: '6px 14px', borderRadius: '8px',
                                                                fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
                                                                display: 'flex', alignItems: 'center', gap: '6px'
                                                            }}
                                                        >
                                                            <Save size={13} /> Save
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() => setEditingOrg({ ...org })}
                                                            style={{
                                                                background: 'rgba(139, 92, 246, 0.08)',
                                                                border: '1px solid rgba(139, 92, 246, 0.15)',
                                                                color: '#8b5cf6', padding: '6px 14px',
                                                                borderRadius: '8px', cursor: 'pointer',
                                                                fontSize: '0.75rem', fontWeight: '600',
                                                                display: 'flex', alignItems: 'center', gap: '6px'
                                                            }}
                                                        >
                                                            <Edit2 size={13} /> Edit
                                                        </button>
                                                        {org.id !== 'saturniq' && (
                                                            <button
                                                                onClick={() => handleDeleteOrg(org.id, org.name)}
                                                                style={{
                                                                    background: 'rgba(239, 68, 68, 0.08)',
                                                                    border: '1px solid rgba(239, 68, 68, 0.15)',
                                                                    color: '#ef4444', padding: '6px 14px',
                                                                    borderRadius: '8px', cursor: 'pointer',
                                                                    fontSize: '0.75rem', fontWeight: '600',
                                                                    display: 'flex', alignItems: 'center', gap: '6px'
                                                                }}
                                                            >
                                                                <Trash2 size={13} /> Delete
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>

                                        </div>
                                    );
                                })}
                            </div>

                            {organizations.length === 0 && !loading && (
                                <div style={{
                                    textAlign: 'center', padding: '60px 24px',
                                    color: 'var(--text-muted)', fontSize: '0.9rem'
                                }}>
                                    <Building2 size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                                    <p>No organizations yet. Create one to get started.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ===== WORKSPACES TAB ===== */}
                    {activeTab === 'workspaces' && (
                        <div>
                            {/* Header with Create Button */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                                <div>
                                    <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem', margin: 0 }}>
                                        Manage workspaces for your organization. Each workspace isolates data and team access.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowCreateWs(!showCreateWs)}
                                    style={{
                                        background: showCreateWs ? 'rgba(239, 68, 68, 0.1)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                        border: showCreateWs ? '1px solid rgba(239, 68, 68, 0.3)' : 'none',
                                        color: showCreateWs ? '#ef4444' : 'white',
                                        padding: '10px 20px', borderRadius: '12px', cursor: 'pointer',
                                        fontWeight: '600', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <Plus size={16} />
                                    {showCreateWs ? 'Cancel' : 'New Workspace'}
                                </button>
                            </div>

                            {/* Create Workspace Form */}
                            {showCreateWs && (
                                <div style={{
                                    background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)',
                                    borderRadius: '16px', padding: '24px', marginBottom: '24px',
                                    animation: 'fadeIn 0.3s ease'
                                }}>
                                    <h4 style={{ color: 'var(--text-main)', marginBottom: '16px', fontSize: '1rem', fontWeight: '600' }}>
                                        Create New Workspace
                                    </h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '6px', fontWeight: '600' }}>Workspace Name *</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                placeholder="e.g. Sales Analytics"
                                                value={newWsName}
                                                onChange={(e) => setNewWsName(e.target.value)}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '6px', fontWeight: '600' }}>Description</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                placeholder="e.g. Workspace for sales team dashboards"
                                                value={newWsDesc}
                                                onChange={(e) => setNewWsDesc(e.target.value)}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => setShowCreateWs(false)}
                                            style={{
                                                background: 'transparent', border: '1px solid var(--glass-border)',
                                                color: 'var(--text-dim)', padding: '10px 20px',
                                                borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleCreateWorkspace}
                                            disabled={loading || !newWsName.trim()}
                                            style={{
                                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                                border: 'none', color: 'white', padding: '10px 24px',
                                                borderRadius: '10px', cursor: 'pointer', fontWeight: '600',
                                                fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px',
                                                opacity: (!newWsName.trim() || loading) ? 0.5 : 1
                                            }}
                                        >
                                            <Check size={16} />
                                            Create Workspace
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Workspaces Grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
                                {workspaces.map(ws => (
                                    <div key={ws.id} style={{
                                        background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)',
                                        borderRadius: '16px', padding: '20px',
                                        transition: 'all 0.2s', position: 'relative'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                                                <div style={{
                                                    width: '36px', height: '36px', borderRadius: '10px',
                                                    background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(37,99,235,0.25))',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                                }}>
                                                    <FolderOpen size={18} style={{ color: '#3b82f6' }} />
                                                </div>
                                                {editingWorkspace?.id === ws.id ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                                                        <input
                                                            value={editingWorkspace.name}
                                                            onChange={e => setEditingWorkspace({ ...editingWorkspace, name: e.target.value })}
                                                            style={{
                                                                background: 'var(--bg-secondary)', border: '1px solid var(--glass-border)',
                                                                color: 'var(--text-main)', padding: '4px 8px', borderRadius: '6px',
                                                                fontSize: '0.9rem', width: '100%'
                                                            }}
                                                            placeholder="Workspace Name"
                                                        />
                                                        <input
                                                            value={editingWorkspace.description}
                                                            onChange={e => setEditingWorkspace({ ...editingWorkspace, description: e.target.value })}
                                                            style={{
                                                                background: 'var(--bg-secondary)', border: '1px solid var(--glass-border)',
                                                                color: 'var(--text-main)', padding: '4px 8px', borderRadius: '6px',
                                                                fontSize: '0.75rem', width: '100%'
                                                            }}
                                                            placeholder="Description"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div style={{ flex: 1 }}>
                                                        <h4 style={{ color: 'var(--text-main)', fontSize: '0.95rem', fontWeight: '600', margin: 0 }}>{ws.name}</h4>
                                                        {ws.description && (
                                                            <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', margin: '2px 0 0 0' }}>{ws.description}</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Stats */}
                                        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                                            <div style={{
                                                flex: 1, background: 'rgba(59, 130, 246, 0.08)', borderRadius: '10px',
                                                padding: '10px 14px', textAlign: 'center'
                                            }}>
                                                <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#3b82f6' }}>{ws.member_count || 0}</div>
                                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Members</div>
                                            </div>
                                            {ws.org_name && (
                                                <div style={{
                                                    flex: 1, background: 'rgba(139, 92, 246, 0.08)', borderRadius: '10px',
                                                    padding: '10px 14px', textAlign: 'center'
                                                }}>
                                                    <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#8b5cf6' }}>{ws.org_name}</div>
                                                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Org</div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            {editingWorkspace?.id === ws.id ? (
                                                <>
                                                    <button
                                                        onClick={() => setEditingWorkspace(null)}
                                                        style={{
                                                            background: 'transparent', border: '1px solid var(--glass-border)',
                                                            color: 'var(--text-dim)', padding: '6px 14px', borderRadius: '8px',
                                                            fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer'
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleUpdateWorkspace}
                                                        style={{
                                                            background: '#3b82f6', border: 'none',
                                                            color: 'white', padding: '6px 14px', borderRadius: '8px',
                                                            fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', gap: '6px'
                                                        }}
                                                    >
                                                        <Save size={13} /> Save
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => setEditingWorkspace({ ...ws })}
                                                        style={{
                                                            background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.2)',
                                                            color: '#3b82f6', padding: '6px 14px', borderRadius: '8px',
                                                            cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem',
                                                            display: 'flex', alignItems: 'center', gap: '4px'
                                                        }}
                                                    >
                                                        <Edit2 size={12} /> Edit
                                                    </button>
                                                    <button
                                                        onClick={() => openMemberModal(ws)}
                                                        style={{
                                                            background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.2)',
                                                            color: '#3b82f6', padding: '6px 14px', borderRadius: '8px',
                                                            cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem',
                                                            display: 'flex', alignItems: 'center', gap: '4px'
                                                        }}
                                                    >
                                                        <Users size={12} /> Manage Members
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteWorkspace(ws.id, ws.name)}
                                                        style={{
                                                            background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)',
                                                            color: '#ef4444', padding: '6px 14px', borderRadius: '8px',
                                                            cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem',
                                                            display: 'flex', alignItems: 'center', gap: '4px'
                                                        }}
                                                    >
                                                        <Trash2 size={12} /> Delete
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                        {/* Created At */}
                                        <div style={{ marginTop: '10px', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                            Created {new Date(ws.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {workspaces.length === 0 && !loading && (
                                <div style={{
                                    textAlign: 'center', padding: '60px 24px',
                                    color: 'var(--text-muted)', fontSize: '0.9rem'
                                }}>
                                    <Layers size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                                    <p>No workspaces yet. Create one to organize your team's data.</p>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>

            {showAccessModal && selectedAsset && (
                <div className="modal-overlay" onClick={() => setShowAccessModal(false)}>
                    <AssetAccessModal
                        asset={selectedAsset}
                        users={users}
                        onClose={() => setShowAccessModal(false)}
                        onSave={handleUpdatePermissions}
                    />
                </div>
            )}

            {/* Workspace Member Management Modal */}
            {memberModalWs && (
                <div className="modal-overlay" onClick={() => setMemberModalWs(null)} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div onClick={e => e.stopPropagation()} style={{
                        background: 'var(--bg-secondary)', border: '1px solid var(--glass-border)',
                        borderRadius: '20px', width: '560px', maxHeight: '80vh', overflow: 'hidden',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', animation: 'fadeIn 0.2s ease'
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            padding: '20px 24px', borderBottom: '1px solid var(--glass-border)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <div>
                                <h3 style={{ color: 'var(--text-main)', margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                                    Manage Members
                                </h3>
                                <p style={{ color: 'var(--text-muted)', margin: '4px 0 0 0', fontSize: '0.8rem' }}>
                                    {memberModalWs.name}
                                </p>
                            </div>
                            <button onClick={() => setMemberModalWs(null)} style={{
                                background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)',
                                color: 'var(--text-dim)', padding: '8px', borderRadius: '10px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center'
                            }}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Add Member Section */}
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--glass-border)' }}>
                            <div style={{ fontSize: '0.75rem', color: 'var(--accent-primary)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <UserPlus size={14} /> Add Member
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <select
                                    className="form-input"
                                    value={addMemberUserId}
                                    onChange={(e) => setAddMemberUserId(e.target.value)}
                                    style={{ flex: 2, minWidth: 0 }}
                                >
                                    <option value="">Select user...</option>
                                    {orgUsers
                                        .filter(u => !wsMembers.some(m => m.user_id === u.id))
                                        .map(u => (
                                            <option key={u.id} value={u.id}>
                                                {u.name || u.email} ({u.email})
                                            </option>
                                        ))
                                    }
                                </select>
                                <select
                                    className="form-input"
                                    value={addMemberRole}
                                    onChange={(e) => setAddMemberRole(e.target.value)}
                                    style={{ flex: 1, minWidth: 0 }}
                                >
                                    <option value="viewer">Viewer</option>
                                    <option value="builder">Builder</option>
                                    <option value="admin">Admin</option>
                                </select>
                                <button
                                    onClick={handleAddMember}
                                    disabled={!addMemberUserId}
                                    style={{
                                        background: addMemberUserId ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'var(--bg-tertiary)',
                                        border: 'none', color: addMemberUserId ? 'white' : 'var(--text-muted)',
                                        padding: '8px 16px', borderRadius: '10px', cursor: addMemberUserId ? 'pointer' : 'default',
                                        fontWeight: '600', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <Plus size={14} /> Add
                                </button>
                            </div>
                            {orgUsers.filter(u => !wsMembers.some(m => m.user_id === u.id)).length === 0 && (
                                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '8px', margin: '8px 0 0 0' }}>
                                    All org users are already members of this workspace.
                                </p>
                            )}
                        </div>

                        {/* Members List */}
                        <div style={{ padding: '20px 24px', maxHeight: '350px', overflowY: 'auto' }}>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                                Members ({wsMembers.length})
                            </div>
                            {wsMembers.length === 0 ? (
                                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '20px' }}>
                                    No members yet. Add users from your organization above.
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {wsMembers.map(member => {
                                        const roleColors = {
                                            admin: { bg: 'rgba(139,92,246,0.1)', text: '#8b5cf6', border: 'rgba(139,92,246,0.2)' },
                                            builder: { bg: 'rgba(59,130,246,0.1)', text: '#3b82f6', border: 'rgba(59,130,246,0.2)' },
                                            viewer: { bg: 'rgba(107,114,128,0.1)', text: '#6b7280', border: 'rgba(107,114,128,0.2)' }
                                        };
                                        const rc = roleColors[member.ws_role] || roleColors.viewer;

                                        return (
                                            <div key={member.membership_id} style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                padding: '10px 14px', background: 'var(--bg-tertiary)',
                                                borderRadius: '12px', border: '1px solid var(--glass-border)'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                                                    <div style={{
                                                        width: '32px', height: '32px', borderRadius: '50%',
                                                        background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        color: 'white', fontSize: '0.75rem', fontWeight: '700', flexShrink: 0
                                                    }}>
                                                        {(member.name || member.email || '?').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={{ color: 'var(--text-main)', fontSize: '0.85rem', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {member.name || 'Unnamed'}
                                                        </div>
                                                        <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {member.email}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                                    <span style={{
                                                        background: rc.bg, color: rc.text, border: `1px solid ${rc.border}`,
                                                        padding: '3px 10px', borderRadius: '20px', fontSize: '0.7rem',
                                                        fontWeight: '600', textTransform: 'capitalize'
                                                    }}>
                                                        {member.ws_role}
                                                    </span>
                                                    <button
                                                        onClick={() => handleRemoveMember(member.user_id, member.name || member.email)}
                                                        style={{
                                                            background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)',
                                                            color: '#ef4444', padding: '4px 8px', borderRadius: '6px',
                                                            cursor: 'pointer', display: 'flex', alignItems: 'center'
                                                        }}
                                                        title="Remove member"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* {showReviewModal && (
                <ReviewModal
                    dashboard={reviewDashboard}
                    users={orgUsers}
                    onClose={() => setShowReviewModal(false)}
                    onSubmit={handleSubmitForReview}
                />
            )} */}
        </div>
    );

    if (isFullPage) return content;

    return (
        <div className="modal-overlay" onClick={onClose}>
            {content}
        </div>
    );
};

export default AdminPanel;
